/* Season results and standings viewer. Reads season JSON files from Files. */
(() => {
  'use strict';

  const KEYS = ['y', 'r', 'c', 'v', 's', 'nd', 'dr', 'q'];
  const DROP_KEY = 'ra-drop';
  const DROPS_KEY = 'ra-drops';
  const DROP_OPTS = [[0, 'No drops'], [1, 'Drop 1 lowest'], [2, 'Drop 2 lowest']];
  const TZ = 'America/Chicago';

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));

  function pad(n, w) { return String(n).padStart(w, '0'); }

  /* English ordinal suffix: 1st, 2nd, 3rd, 4th, 11th-13th, 21st. */
  function ordSuffix(n) {
    const v = Math.abs(parseInt(n, 10)) % 100;
    if (v >= 11 && v <= 13) return 'th';
    return ['th', 'st', 'nd', 'rd'][v % 10] || 'th';
  }

  /* Escaped ordinal markup; non-numeric values pass through escaped. */
  function ordHTML(n) {
    if (n == null || n === '' || isNaN(parseInt(n, 10))) return esc(n);
    return `${esc(n)}<span class="ra-ord">${esc(ordSuffix(n))}</span>`;
  }

  /* Escaped points value with a unit suffix. */
  function ptsHTML(v) {
    return `${esc(v)}<span class="ra-u"> pts</span>`;
  }

  /* Stored drop count 0, 1 or 2; null means the season's rule. The legacy key held '0' for every round counted. */
  function readDrop() {
    try {
      const v = localStorage.getItem(DROPS_KEY);
      if (v === '0' || v === '1' || v === '2') return Number(v);
      return localStorage.getItem(DROP_KEY) === '0' ? 0 : null;
    } catch (err) { return null; }
  }

  function saveDrop(k) {
    try { localStorage.setItem(DROPS_KEY, String(k)); localStorage.removeItem(DROP_KEY); } catch (err) { /* storage unavailable */ }
  }

  /* Drop count from the query: dr=0|1|2, or legacy nd=1 for every round counted. undefined when absent. */
  function urlDrop(p) {
    if (p.has('dr')) { const v = p.get('dr'); return v === '0' || v === '1' || v === '2' ? Number(v) : null; }
    if (p.has('nd')) return p.get('nd') === '1' ? 0 : null;
    return undefined;
  }

  /* ISO timestamp to "Oct 4, 2026, 3:12 PM CT"; empty string when absent or invalid. */
  function fmtStamp(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    try {
      return d.toLocaleString('en-US', { timeZone: TZ, month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) + ' CT';
    } catch (err) {
      return '';
    }
  }

  function fmtT(sec) {
    if (sec == null || isNaN(sec)) return '';
    const neg = sec < 0;
    let ms = Math.round(Math.abs(sec) * 1000);
    const h = Math.floor(ms / 3600000); ms -= h * 3600000;
    const m = Math.floor(ms / 60000); ms -= m * 60000;
    const s = Math.floor(ms / 1000); ms -= s * 1000;
    const tail = pad(s, 2) + '.' + pad(ms, 3);
    return (neg ? '-' : '') + (h ? h + ':' + pad(m, 2) + ':' + tail : m + ':' + tail);
  }

  /* Signed lap delta in seconds, three decimals. */
  function fmtDelta(d) {
    return (d < 0 ? '-' : '+') + Math.abs(d).toFixed(3) + ' sec';
  }

  /* Class standings with k rounds dropped, from each rider's round cells [pos, pts, status].
     Dropped: lowest points first; at equal points a round without a finish before a finish, then the
     earliest round. Applied only when the rider has more rounds than k.
     Order: total, then countback on finishes (more 1sts, then 2nds, ... 40ths), then name.
     Equal total and countback share a position. Returns rows in order and per-row { x, total, pos }. */
  const ruleCache = new WeakMap();
  function standingsFor(c, k) {
    let byK = ruleCache.get(c);
    if (!byK) ruleCache.set(c, (byK = {}));
    if (byK[k]) return byK[k];
    const info = new Map();
    const list = c.rows.map((r) => {
      const rd = r.rd || [];
      const pts = rd.map((cell) => cell[1] || 0);
      const fin = (i) => (rd[i][0] == null ? 0 : 1);
      const x = k && pts.length > k
        ? pts.map((v, i) => i).sort((a, b) => pts[a] - pts[b] || fin(a) - fin(b) || a - b).slice(0, k).sort((a, b) => a - b)
        : [];
      const total = pts.reduce((a, v, i) => a + (x.includes(i) ? 0 : v), 0);
      const cb = new Array(40).fill(0);
      rd.forEach((cell) => { if (cell[0] >= 1 && cell[0] <= 40) cb[cell[0] - 1]++; });
      const o = { r, x, total, cb, name: String(r.r || '').toLowerCase(), pos: null };
      info.set(r, o);
      return o;
    });
    const cmp = (a, b) => {
      if (a.total !== b.total) return b.total - a.total;
      for (let i = 0; i < 40; i++) if (a.cb[i] !== b.cb[i]) return b.cb[i] - a.cb[i];
      return 0;
    };
    list.sort((a, b) => cmp(a, b) || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
    list.forEach((o, i) => { o.pos = i && !cmp(list[i - 1], o) ? list[i - 1].pos : i + 1; });
    const out = { rows: list.map((o) => o.r), info };
    byK[k] = out;
    return out;
  }

  function fmtGap(sec) {
    if (sec == null) return '';
    return '+' + (sec < 60 ? sec.toFixed(3) + ' sec' : fmtT(sec));
  }

  /* Leader's margin over 2nd as a negative gap: laps when 2nd is laps down, else time; '—' when unknown. */
  function leadGap(lead, second, sec) {
    if (!lead || !second) return '—';
    const down = lead.l != null && second.l != null ? lead.l - second.l : 0;
    if (down > 0) return `-${down} lap${down === 1 ? '' : 's'}`;
    if (sec == null || !isFinite(sec) || sec < 0) return '—';
    return '-' + (sec < 60 ? sec.toFixed(3) + ' sec' : fmtT(sec));
  }

  /* Seconds between two total times held as seconds, via whole milliseconds. */
  function secDiff(a, b) {
    if (typeof a !== 'number' || typeof b !== 'number') return null;
    return (Math.round(a * 1000) - Math.round(b * 1000)) / 1000;
  }

  /* Today's date in Central time as YYYY-MM-DD. */
  function todayCT() {
    try { return new Date().toLocaleDateString('en-CA', { timeZone: TZ }); } catch (err) { return new Date().toISOString().slice(0, 10); }
  }

  /* Rider link id: plate number plus a 4-character base-36 hash of the name. */
  function slugOf(r) {
    let h = 0;
    for (const ch of String(r.r || '').toLowerCase()) h = (Math.imul(h, 31) + ch.codePointAt(0)) >>> 0;
    const n = String(r.n).toLowerCase().replace(/[^a-z0-9]+/g, '');
    return n + '-' + (h % 1679616).toString(36).padStart(4, '0');
  }

  /* Previous link id format (number and name), still accepted from older links. */
  function oldSlugOf(r) {
    return (String(r.n) + '-' + String(r.r || '')).toLowerCase().replace(/[‘’']/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function hashParams() {
    return new URLSearchParams(location.hash.replace(/^#/, ''));
  }

  const PUMP = '<svg class="gm-pump" viewBox="0 0 24 24" focusable="false"><path fill="currentColor" d="M4 3h9a1 1 0 0 1 1 1v8h1.5A2.5 2.5 0 0 1 18 14.5V18a1 1 0 0 0 2 0V9.4l-2.7-2.7 1.4-1.4 3 3c.2.2.3.4.3.7V18a3 3 0 0 1-6 0v-3.5a.5.5 0 0 0-.5-.5H14v7h1v2H2v-2h1V4a1 1 0 0 1 1-1zm1 2v5h7V5H5z"/></svg>';

  function fmtDate(iso, long) {
    if (!iso) return '';
    const d = new Date(iso + 'T12:00:00');
    return d.toLocaleDateString('en-US', long ? { month: 'long', day: 'numeric', year: 'numeric' } : { month: 'short', day: 'numeric' });
  }

  /* Date range as "Sep 26–27" or "Feb 28 – Mar 1"; long adds the year. */
  function fmtSpan(sp, long) {
    if (!sp || !sp.s) return '';
    if (!sp.e || sp.e === sp.s) return fmtDate(sp.s, long);
    const a = new Date(sp.s + 'T12:00:00');
    const b = new Date(sp.e + 'T12:00:00');
    const mon = (d) => d.toLocaleDateString('en-US', { month: long ? 'long' : 'short' });
    const yr = long ? ', ' + b.getFullYear() : '';
    if (a.getFullYear() !== b.getFullYear()) return fmtDate(sp.s, long) + ' – ' + fmtDate(sp.e, long);
    if (a.getMonth() === b.getMonth()) return mon(a) + ' ' + a.getDate() + '–' + b.getDate() + yr;
    return mon(a) + ' ' + a.getDate() + ' – ' + mon(b) + ' ' + b.getDate() + yr;
  }

  /* Weekday span as "Sat" or "Sat–Sun". */
  function fmtDays(sp) {
    if (!sp || !sp.s) return '';
    const wd = (iso) => new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' });
    return !sp.e || sp.e === sp.s ? wd(sp.s) : wd(sp.s) + '–' + wd(sp.e);
  }

  function addDays(iso, n) {
    const d = new Date(iso + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
  }

  function loc(r) {
    const co = r.co && r.co.length > 2 && r.co !== r.s && r.co !== 'USA' ? ' ' + r.co : '';
    return [r.c, r.s].filter(Boolean).join(', ') + co;
  }

  function matches(r, q) {
    if (!q) return true;
    const t = q.toLowerCase().replace(/[‘’]/g, "'").replace(/^#/, '');
    return String(r.n || '').toLowerCase() === t || String(r.n || '').startsWith(t) || (r.r || '').toLowerCase().replace(/[‘’]/g, "'").includes(t);
  }

  /* Round file rows: a best-lap index on lap 1 (includes the start) is cleared, so no row has a best without a lap 2. */
  function normRound(data) {
    if (!data || !data.cls) return data;
    Object.values(data.cls).forEach((c) => (c && c.ses || []).forEach((ses) => (ses && ses.rows || []).forEach((r) => {
      if (r && r.b != null && !(r.b > 0)) {
        r.b = null;
        if (r.lt && r.lt.length) r.bl = null;
      }
    })));
    return data;
  }

  const cache = new Map();
  function getJSON(url) {
    if (!cache.has(url)) {
      cache.set(url, fetch(url, { credentials: 'omit' }).then((res) => {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      }).then(normRound).catch((err) => { cache.delete(url); throw err; }));
    }
    return cache.get(url);
  }

  /* Lap-time tick label without milliseconds. */
  function fmtTick(sec) {
    const t = Math.round(sec);
    const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), x = t % 60;
    return h ? h + ':' + pad(m, 2) + ':' + pad(x, 2) : m + ':' + pad(x, 2);
  }

  /* Tick step in seconds giving about four intervals over the span. */
  function niceStep(span) {
    const steps = [1, 2, 5, 10, 15, 20, 30, 60, 120, 300, 600, 900, 1800, 3600];
    return steps.find((v) => span / v <= 5) || 3600;
  }

  /* Indexes of x-axis lap ticks, thinned so labels keep at least minGap px apart. */
  function lapTicks(n, width, minGap) {
    const every = Math.max(1, Math.ceil((minGap * (n - 1)) / Math.max(width, 1)));
    const out = [];
    for (let i = 0; i < n; i += every) out.push(i);
    if (out[out.length - 1] !== n - 1) {
      if ((n - 1 - out[out.length - 1]) * (width / (n - 1)) < minGap && out.length > 1) out.pop();
      out.push(n - 1);
    }
    return out;
  }

  /* Moto finishes, e.g. [1, 3] -> "1-3", [2, 'DNS'] -> "2-DNS". */
  function motoText(mp) {
    return (mp || []).join('-');
  }

  function hasLaps(r) {
    return !!(r && r.lt && r.lt.some((v) => typeof v === 'number' && v > 0));
  }

  function lapItems(r, classBest) {
    const lt = r.lt || [];
    const best = r.b != null ? lt[r.b] : null;
    return lt.map((v, i) => {
      const d = best != null && i !== r.b ? fmtDelta(v - best) : (i === r.b ? 'best' : '');
      const cb = classBest != null && Math.abs(v - classBest) < 0.0005;
      return `<li class="${i === r.b ? 'is-best' : ''}${cb ? ' is-cbest' : ''}"><span>Lap ${esc(i + 1)}</span><b>${esc(fmtT(v))}</b><em>${esc(d)}</em></li>`;
    }).join('');
  }

  function bestOf(rows) {
    const b = rows.filter((r) => r.b > 0 && r.lt && typeof r.lt[r.b] === 'number').map((r) => r.lt[r.b]);
    return b.length ? Math.min.apply(null, b) : null;
  }

  /* Lap time truncated to tenths, e.g. 407.451 -> "6:47.4". */
  function fmtT1(sec) {
    const t = Math.round(sec * 10);
    const h = Math.floor(t / 36000), m = Math.floor((t % 36000) / 600), x = Math.floor((t % 600) / 10);
    const tail = pad(x, 2) + '.' + (t % 10);
    return h ? h + ':' + pad(m, 2) + ':' + tail : m + ':' + tail;
  }

  /* Time axis for lap charts: tick step of 30 s or whole minutes giving 3-5 ticks, bounds snapped to the step. */
  function timeAxis(vals) {
    const mn = Math.min.apply(null, vals), mx = Math.max.apply(null, vals);
    const steps = [30, 60, 120, 300, 600, 900, 1800, 3600];
    let step = 3600, lo = null, hi = null;
    for (const v of steps) {
      const a = Math.floor(mn / v) * v, b = Math.max(Math.ceil(mx / v) * v, a + v);
      if ((b - a) / v + 1 <= 5) { step = v; lo = a; hi = b; break; }
    }
    if (lo == null) { lo = Math.floor(mn / step) * step; hi = Math.max(Math.ceil(mx / step) * step, lo + step); }
    if ((hi - lo) / step + 1 < 3) hi += step;
    const ticks = [];
    for (let v = lo; v <= hi + 0.001; v += step) ticks.push(v);
    return { lo, hi, ticks };
  }

  /* Shared frame for lap charts: plot box, scales, y gridlines with m:ss labels, x lap numbers. */
  function chartFrame(vals, n, W, H) {
    const ax = timeAxis(vals);
    const lab = ax.ticks.map(fmtTick);
    const L = 10 + Math.max.apply(null, lab.map((t) => t.length)) * 6, R = 10, T = 20, B = 32;
    const x = (i) => (n === 1 ? (L + W - R) / 2 : L + 6 + (i * (W - L - R - 6)) / (n - 1));
    const y = (v) => T + ((v - ax.lo) / (ax.hi - ax.lo)) * (H - T - B);
    let grid = '';
    ax.ticks.forEach((v, k) => {
      const yy = y(v).toFixed(1);
      grid += `<line class="ra-chart-g" x1="${L}" x2="${W - R}" y1="${yy}" y2="${yy}"/><text class="ra-chart-y" x="${L - 5}" y="${(y(v) + 3.5).toFixed(1)}">${esc(lab[k])}</text>`;
    });
    const every = n <= 12 ? 1 : Math.ceil(n / 12);
    for (let i = 0; i < n; i++) {
      if (i % every === 0) grid += `<text class="ra-chart-x" x="${x(i).toFixed(1)}" y="${H - 4}">${esc(i + 1)}</text>`;
    }
    return { L, R, T, B, x, y, grid };
  }

  /* Point label above (dir -1) or below (dir 1) a point, anchored to stay inside the plot; returns markup and its box. */
  function ptLabel(px, py, text, cls, f, W, dir) {
    const w = text.length * 6.2, h = 10;
    let anchor = 'middle', x0 = px - w / 2;
    if (x0 < f.L + 2) { anchor = 'start'; x0 = Math.max(px - 4, f.L + 2); }
    else if (px + w / 2 > W - 2) { anchor = 'end'; x0 = Math.min(px + 4, W - 2) - w; }
    const tx = anchor === 'start' ? x0 : anchor === 'end' ? x0 + w : px;
    const base = dir < 0 ? py - 8 : py + 17;
    return {
      svg: `<text class="ra-chart-pt ${cls}${anchor === 'middle' ? '' : ' is-' + anchor}" x="${tx.toFixed(1)}" y="${base.toFixed(1)}">${esc(text)}</text>`,
      box: [x0, base - h, x0 + w, base + 2],
    };
  }

  function boxHit(a, b) {
    return a[0] < b[2] + 3 && b[0] < a[2] + 3 && a[1] < b[3] + 1 && b[1] < a[3] + 1;
  }

  function isCB(v, classBest) {
    return classBest != null && typeof v === 'number' && Math.abs(v - classBest) < 0.0005;
  }

  /* Whether a class-best point is drawn among the given lap series. */
  function drawsCB(classBest, ...series) {
    return series.some((lt) => (lt || []).some((v) => isCB(v, classBest)));
  }

  /* Index of the slowest lap from lap 2 on (lap 1 includes the start); null with fewer than two laps. */
  function slowLap(lt) {
    let k = null;
    for (let i = 1; i < lt.length; i++) {
      if (typeof lt[i] === 'number' && isFinite(lt[i]) && (k == null || lt[i] > lt[k])) k = i;
    }
    return k;
  }

  function lapChart(lt, best, classBest) {
    if (!lt || !lt.length) return '';
    const W = 320, H = 132;
    const f = chartFrame(lt, lt.length, W, H);
    const { x, y } = f;
    const hasB = best != null && best > 0 && typeof lt[best] === 'number';
    const pts = lt.map((v, i) => x(i).toFixed(1) + ',' + y(v).toFixed(1)).join(' ');
    let dots = '';
    lt.forEach((v, i) => {
      const cls = i === best ? 'is-best' : '';
      const cb = isCB(v, classBest) ? ' is-cbest' : '';
      dots += `<circle class="${cls}${cb}" cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="${i === best ? 4 : 2.6}"><title>Lap ${esc(i + 1)}: ${esc(fmtT(v))}</title></circle>`;
    });
    const slow = slowLap(lt);
    const showSlow = slow != null && slow !== best && (!hasB || lt[slow] > lt[best]);
    let labels = '';
    if (hasB) labels += ptLabel(x(best), y(lt[best]), fmtT1(lt[best]), 'is-best', f, W, -1).svg;
    if (showSlow) labels += ptLabel(x(slow), y(lt[slow]), fmtT1(lt[slow]), 'is-slow', f, W, 1).svg;
    const base = hasB ? `<line class="ra-chart-base" x1="${f.L}" x2="${W - f.R}" y1="${y(lt[best]).toFixed(1)}" y2="${y(lt[best]).toFixed(1)}"/>` : '';
    const aria = 'Lap times, fastest at top.' + (hasB ? ` Best ${fmtT(lt[best])} on lap ${best + 1}.` : '') + (showSlow ? ` Slowest ${fmtT(lt[slow])} on lap ${slow + 1}.` : '');
    return `<svg class="ra-chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(aria)}">
      ${f.grid}${base}
      <polyline points="${pts}"/>${dots}${labels}</svg>`;
  }

  /* Signed difference in seconds: "+X.XXX sec" under 60 s, "+m:ss.mmm" from 60 s. */
  function fmtDiff(d) {
    if (d == null || isNaN(d)) return '';
    const a = Math.abs(d);
    const sign = d > 0 ? '+' : d < 0 ? '-' : '';
    return sign + (a < 60 ? a.toFixed(3) + ' sec' : fmtT(a));
  }

  /* Two riders' lap times on one shared scale; best lap and class best marked per rider, each best labelled with its time. */
  function lapChart2(la, ba, lb, bb, classBest, na, nb) {
    const A = la || [], Bv = lb || [];
    const n = Math.max(A.length, Bv.length);
    if (!n) return '';
    const W = 320, H = 144;
    const all = A.concat(Bv).filter((v) => typeof v === 'number' && isFinite(v));
    const f = chartFrame(all, n, W, H);
    const { x, y } = f;
    const series = (lt, best, cls, who) => {
      const ok = lt.map((v, i) => [v, i]).filter((q) => typeof q[0] === 'number' && isFinite(q[0]));
      const pts = ok.map((q) => x(q[1]).toFixed(1) + ',' + y(q[0]).toFixed(1)).join(' ');
      const dots = ok.map(([v, i]) => {
        const cb = isCB(v, classBest) ? ' is-cbest' : '';
        return `<circle class="${cls}${i === best ? ' is-best' : ''}${cb}" cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="${i === best ? 4.2 : 2.4}"><title>${who ? esc(who) + ' · ' : ''}Lap ${esc(i + 1)}: ${esc(fmtT(v))}</title></circle>`;
      }).join('');
      return { line: ok.length > 1 ? `<polyline class="${cls}" points="${pts}"/>` : '', dots };
    };
    const sa = series(A, ba, 'is-a', na), sb = series(Bv, bb, 'is-b', nb);
    const bests = [[A, ba, 'is-a'], [Bv, bb, 'is-b']]
      .filter((q) => q[1] != null && q[1] > 0 && typeof q[0][q[1]] === 'number')
      .sort((p, q) => p[0][p[1]] - q[0][q[1]]);
    let labels = '';
    const boxes = [];
    bests.forEach(([lt, i, cls]) => {
      let lab = ptLabel(x(i), y(lt[i]), fmtT1(lt[i]), cls, f, W, -1);
      if (boxes.some((b) => boxHit(b, lab.box))) lab = ptLabel(x(i), y(lt[i]), fmtT1(lt[i]), cls, f, W, 1);
      boxes.push(lab.box);
      labels += lab.svg;
    });
    return `<svg class="ra-chart ra-chart--vs" viewBox="0 0 ${W} ${H}" role="img" aria-label="Lap times of both riders, fastest at top.">
      ${f.grid}${sb.line}${sa.line}${sb.dots}${sa.dots}${labels}</svg>`;
  }

  /* Running order after each lap from per-lap times. For lap k: leader row index, position and
     gap to the leader per row index, the leader's margin over 2nd (null when alone on the lap),
     and whether the leader differs from lap k-1. */
  function lapOrder(rows) {
    const cum = rows.map((r) => {
      const out = [];
      let t = 0;
      for (const v of r.lt || []) {
        if (typeof v !== 'number' || !isFinite(v) || v <= 0) break;
        t += Math.round(v * 1000);
        out.push(t);
      }
      return out;
    });
    const n = cum.reduce((m, c) => Math.max(m, c.length), 0);
    const laps = [];
    for (let k = 0; k < n; k++) {
      const on = [];
      cum.forEach((c, i) => { if (c.length > k) on.push(i); });
      on.sort((a, b) => (cum[a][k] - cum[b][k]) || (a - b));
      const lead = on[0];
      const pos = new Map();
      const gap = new Map();
      on.forEach((i, j) => { pos.set(i, j + 1); gap.set(i, cum[i][k] - cum[lead][k]); });
      const ahead = on.length > 1 ? gap.get(on[1]) : null;
      laps.push({ lead, t: cum[lead][k] / 1000, pos, gap, ahead, chg: k > 0 && lead !== laps[k - 1].lead });
    }
    return laps;
  }

  /* Lap gap from whole milliseconds: one decimal, three below 0.05 s. */
  function lapGap(ms) {
    return (ms < 50 ? (ms / 1000).toFixed(3) : (Math.round(ms / 100) / 10).toFixed(1)) + ' sec';
  }

  class ResultsApp {
    constructor(el) {
      this.el = el;
      this.root = el.querySelector('[data-ra-root]');
      this.cfg = JSON.parse(el.querySelector('[data-ra-cfg]').textContent);
      this.open = new Set();
      this.tab = '';
      this.state = {};
      this.idx = null;
      this.st = null;
      this.ev = null;
      this.bucket = Math.floor(Date.now() / 600000);
    }

    async init() {
      this.readURL();
      window.addEventListener('popstate', () => { this.readURL(); this.load(); });
      this.root.addEventListener('click', (e) => this.onClick(e));
      this.root.addEventListener('input', (e) => this.onInput(e));
      this.root.addEventListener('change', (e) => this.onChange(e));
      this.root.addEventListener('keydown', (e) => this.onKey(e));
      window.addEventListener('hashchange', () => this.syncRider());
      await this.load();
    }

    seasonCfg() {
      const list = this.cfg.seasons || [];
      return list.find((s) => s.y === this.state.y) || list[0];
    }

    base(url) { return url.split('?')[0].replace(/[^/]*$/, ''); }

    readURL() {
      const p = new URLSearchParams(location.search);
      this.state = {
        y: p.get('y') || '',
        r: p.get('r') ? parseInt(p.get('r'), 10) : null,
        c: p.get('c') || '',
        v: p.get('v') === 'standings' || p.get('view') === 'standings' || /^#standings\b/.test(location.hash) || hashParams().has('rider') ? 'standings' : 'results',
        s: p.get('s') || '',
        dr: urlDrop(p) !== undefined ? urlDrop(p) : readDrop(),
        q: p.get('q') || '',
      };
    }

    writeURL(push) {
      const p = new URLSearchParams(location.search);
      KEYS.forEach((k) => p.delete(k));
      p.delete('view');
      const s = this.state;
      const first = (this.cfg.seasons || [])[0];
      if (s.y && first && s.y !== first.y) p.set('y', s.y);
      if (s.v === 'results' && s.r) p.set('r', s.r);
      if (s.c) p.set('c', s.c);
      if (s.v === 'standings') p.set('v', 'standings');
      if (s.v === 'results' && s.s && s.s !== 'ov') p.set('s', s.s);
      if (s.v === 'standings' && s.dr != null && (!this.st || (this.ruleN() && s.dr !== this.ruleN()))) p.set('dr', String(s.dr));
      if (s.q) p.set('q', s.q);
      const qs = p.toString();
      const url = location.pathname + (qs ? '?' + qs : '') + location.hash;
      if (url === location.pathname + location.search + location.hash) return;
      history[push ? 'pushState' : 'replaceState'](null, '', url);
    }

    async load() {
      const sc = this.seasonCfg();
      if (!sc) return this.fail('No season is set up for this page.');
      if (!this.idx || this.idx.season !== sc.y) {
        try {
          const u = sc.u + (sc.u.includes('?') ? '&' : '?') + 't=' + this.bucket;
          this.idx = await getJSON(u);
          this.idxBase = this.base(sc.u);
          this.st = null;
          this.shell();
        } catch (err) {
          return this.fail('Results could not be loaded right now.');
        }
      }
      const s = this.state;
      s.y = this.idx.season;
      const evs = this.idx.ev || [];
      if (!evs.length) return this.fail('No results have been posted for this season yet.');
      if (!evs.some((e) => e.r === s.r)) s.r = this.latest().r;
      await this.render();
    }

    latest() {
      const evs = this.idx.ev;
      const done = evs.filter((e) => e.done !== false);
      return (done.length ? done : evs)[(done.length ? done : evs).length - 1];
    }

    event() { return this.idx.ev.find((e) => e.r === this.state.r); }

    /* Published schedule dates for a round. The schedule range is used when the timing
       date falls between 2 days before its start and 1 day after its end; otherwise the
       timing date is kept. */
    span(e) {
      const own = { s: e.d || '', e: e.d || '' };
      if (!e.d) return own;
      const sc = this.seasonCfg() || {};
      const x = (sc.sched || []).find((y) => y.n === e.r && y.s);
      if (!x) return own;
      const end = x.e || x.s;
      return e.d >= addDays(x.s, -2) && e.d <= addDays(end, 1) ? { s: x.s, e: end } : own;
    }

    when(e, long) { return fmtSpan(this.span(e), long); }

    fail(msg) {
      this.root.innerHTML = `<div class="ra-msg ra-msg--err"><p>${esc(msg)}</p><a class="ra-btn" href="${esc(this.cfg.timing)}" target="_blank" rel="noopener">Open official timing</a></div>`;
    }

    shell() {
      const idx = this.idx;
      const lat = this.latest();
      const seasons = this.cfg.seasons || [];
      const stamp = fmtStamp(idx.u || idx.updated);
      const latSpan = this.span(lat);
      const latDays = fmtDays(latSpan);
      const latId = 'ra-lat-h-' + esc(this.el.id);
      const seasonSel = seasons.length > 1
        ? `<label class="ra-season"><span class="visually-hidden">Season</span><select data-ra-season>${seasons.map((s) => `<option value="${esc(s.y)}"${s.y === idx.season ? ' selected' : ''}>${esc(s.y)}</option>`).join('')}</select></label>`
        : '';
      this.root.innerHTML = `
        <div class="ra-hero">
          <div class="ra-hero-main">
            <p class="ra-k">${esc(idx.season)} season ${seasonSel}</p>
            <h2 class="ra-hero-h">${esc(idx.series || this.cfg.series + ' ' + idx.season)}</h2>
            <p class="ra-hero-d">${esc(idx.ev.length)} round${idx.ev.length === 1 ? '' : 's'}</p>
            ${this.hud()}
          </div>
          <section class="ra-latest" aria-labelledby="${latId}">
            <span class="ra-latest-k"><span aria-hidden="true">R${esc(lat.r)} · Latest</span><span class="visually-hidden">Latest results, round ${esc(lat.r)}</span></span>
            <h3 class="ra-latest-h" id="${latId}">${esc(lat.venue)}</h3>
            <p class="ra-latest-d">${latDays ? `<span>${esc(latDays)}</span> · ` : ''}<time datetime="${esc(latSpan.s)}">${esc(fmtSpan(latSpan) + (latSpan.s ? ', ' + (latSpan.e || latSpan.s).slice(0, 4) : ''))}</time>${lat.city ? ' · ' + esc(lat.city) : ''}</p>
            ${stamp ? `<p class="ra-latest-u">Data updated ${esc(stamp)}</p>` : ''}
            <button type="button" class="ra-latest-go" data-ra-go="${esc(lat.r)}" aria-describedby="${latId}">View results<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true" focusable="false"><path d="M3 8h9M8 3l5 5-5 5"/></svg></button>
          </section>
        </div>
        <div class="ra-bar">
          <div class="ra-views" role="group" aria-label="View">
            <button type="button" class="ra-view" data-ra-v="results">Results</button>
            <button type="button" class="ra-view" data-ra-v="standings">Standings</button>
          </div>
          <label class="ra-search">
            <span class="visually-hidden">Find a rider by name or number</span>
            <input type="search" data-ra-q placeholder="Find rider or #" autocomplete="off" enterkeyhint="search">
          </label>
        </div>
        <div class="ra-rounds" role="group" aria-label="Round" data-ra-rounds></div>
        <div class="ra-groups" role="group" aria-label="Class group" data-ra-groups></div>
        <div class="ra-classes" role="group" aria-label="Class" data-ra-classes></div>
        <div class="ra-panel" data-ra-panel></div>`;
      const up = this.el.querySelector('[data-ra-updated]');
      if (up) up.textContent = stamp ? 'Updated ' + stamp : '';
    }

    /* Scheduled rounds for the season with done / next / lock state, as on the schedule page. */
    schedState() {
      const sc = this.seasonCfg() || {};
      const today = todayCT();
      const sched = (sc.sched || []).slice();
      /* Posted rounds missing from the schedule count as finished. */
      if (sched.length && this.idx) {
        (this.idx.ev || []).forEach((e) => { if (!sched.some((x) => x.n === e.r)) sched.push({ n: e.r, s: e.d, e: e.d }); });
      }
      const list = sched.sort((a, b) => String(a.s || '9999').localeCompare(String(b.s || '9999')) || (a.n || 0) - (b.n || 0))
        .map((x) => { const end = x.e || x.s || ''; return { n: x.n, s: x.s || '', e: end, reg: x.reg || '', st: end && end < today ? 'done' : 'lock' }; });
      const nx = list.find((x) => x.st === 'lock' && x.e);
      if (nx) nx.st = 'next';
      return list;
    }

    hud() {
      const list = this.schedState();
      const rows = list.length ? list : this.idx.ev.map((e) => ({ n: e.r, st: 'done' }));
      const segs = rows.map((x) => x.st);
      const done = segs.filter((x) => x === 'done').length;
      const bar = rows.map((x) => {
        const lab = x.n != null ? `<span class="ra-rd" aria-hidden="true"><em>RD </em>${esc(x.n)}</span>` : '';
        return `<i${x.st === 'done' ? ' class="d"' : x.st === 'next' ? ' class="n"' : ''}>${lab}</i>`;
      }).join('');
      return `<div class="gm-hud ra-hud"><b>${esc(done)}/${esc(segs.length)} rounds finished</b><span class="gm-fuel"><span aria-hidden="true">${PUMP}</span><span class="gm-ef" aria-hidden="true">E</span><span class="gm-bar">${bar}</span><span class="gm-ef gm-ef--f" aria-hidden="true">F</span></span></div>`;
    }

    classList() {
      if (this.state.v === 'standings') {
        if (!this.st) return [];
        return Object.keys(this.st.cls).map((k) => ({ k, n: this.st.cls[k].n, grp: this.st.cls[k].grp, c: this.st.cls[k].rows.length }));
      }
      const e = this.event();
      return e ? e.cls : [];
    }

    async render() {
      const s = this.state;
      const panel = this.root.querySelector('[data-ra-panel]');
      if (!panel) return;
      this.root.querySelectorAll('[data-ra-v]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.raV === s.v)));
      this.el.dataset.raView = s.v;
      const qi = this.root.querySelector('[data-ra-q]');
      if (qi && qi.value !== s.q) qi.value = s.q;
      this.renderRounds();
      if (s.v === 'standings' && !this.st) {
        panel.innerHTML = this.loadingRows();
        try {
          this.st = await getJSON(this.idxBase + this.idx.st + (this.idx.stv ? '?v=' + this.idx.stv : ''));
        } catch (err) {
          this.renderClasses();
          panel.innerHTML = this.errBox('Standings could not be loaded right now.');
          return;
        }
      }
      const list = this.classList();
      if (!list.some((c) => c.k === s.c)) {
        const prefs = String(this.cfg.defaultClass || '').split(',').map((x) => x.trim()).filter(Boolean);
        const def = prefs.map((k) => list.find((c) => c.k === k)).find(Boolean) || list[0];
        s.c = def ? def.k : '';
      }
      this.renderClasses();
      this.writeURL(false);
      if (s.v === 'standings') {
        this.renderStandings(panel);
        this.syncRider();
        return;
      }
      const e = this.event();
      panel.innerHTML = this.loadingRows();
      let data;
      try {
        data = await getJSON(this.idxBase + e.f + (e.fv ? '?v=' + e.fv : ''));
      } catch (err) {
        panel.innerHTML = this.errBox('This round could not be loaded right now.');
        return;
      }
      if (this.state.r !== e.r || this.state.v !== 'results') return;
      this.renderResults(panel, e, data);
    }

    loadingRows() {
      return '<div class="ra-loading" role="status"><span class="ra-skel ra-skel--row"></span><span class="ra-skel ra-skel--row"></span><span class="ra-skel ra-skel--row"></span><span class="visually-hidden">Loading</span></div>';
    }

    errBox(msg) {
      return `<div class="ra-msg ra-msg--err"><p>${esc(msg)}</p><a class="ra-btn" href="${esc(this.cfg.timing)}" target="_blank" rel="noopener">Open official timing</a></div>`;
    }

    renderRounds() {
      const box = this.root.querySelector('[data-ra-rounds]');
      const s = this.state;
      box.hidden = s.v === 'standings';
      if (box.hidden) delete box.dataset.raPlaced;
      const lat = this.latest();
      box.innerHTML = this.idx.ev.map((e) => `
        <button type="button" class="ra-round${e.r === lat.r ? ' is-latest' : ''}" data-ra-r="${esc(e.r)}" aria-pressed="${e.r === s.r}">
          <span class="ra-round-n">R${esc(e.r)}</span>
          <span class="ra-round-v">${esc(e.venue)}</span>
          <span class="ra-round-d">${esc(this.when(e))}</span>
        </button>`).join('');
      this.placeRounds(box);
    }

    /* First visible render of a season's strip: scroll it so the selected round sits at the right edge. */
    placeRounds(box) {
      if (box.hidden || box.dataset.raPlaced || !box.clientWidth) return;
      box.dataset.raPlaced = '1';
      if (box.scrollWidth <= box.clientWidth) return;
      const t = box.querySelector('[aria-pressed="true"]') || box.lastElementChild;
      if (!t) return;
      const br = box.getBoundingClientRect();
      const tr = t.getBoundingClientRect();
      const x = Math.max(0, Math.min(box.scrollWidth - box.clientWidth, Math.ceil(tr.right - br.left + box.scrollLeft - box.clientWidth + 2)));
      try { box.scrollTo({ left: x, behavior: 'instant' }); } catch (err) { box.scrollLeft = x; }
    }

    renderClasses() {
      const s = this.state;
      const list = this.classList();
      const order = this.idx.groups || [];
      const groups = [];
      list.forEach((c) => { if (!groups.includes(c.grp)) groups.push(c.grp); });
      groups.sort((a, b) => order.indexOf(a) - order.indexOf(b));
      const cur = (list.find((c) => c.k === s.c) || {}).grp;
      this.root.querySelector('[data-ra-groups]').innerHTML = groups.map((g) => {
        const n = list.filter((c) => c.grp === g).reduce((a, c) => a + (c.c || 0), 0);
        return `<button type="button" class="ra-group" data-ra-g="${esc(g)}" aria-pressed="${g === cur}">${esc(g)} <span>${esc(n)}</span></button>`;
      }).join('');
      this.root.querySelector('[data-ra-classes]').innerHTML = list.filter((c) => c.grp === cur).map((c) => `
        <button type="button" class="ra-class" data-ra-c="${esc(c.k)}" aria-pressed="${c.k === s.c}">${esc(c.n)} <span>${esc(c.c)}</span></button>`).join('');
    }

    sessionsOf(cls) {
      return (cls.ses || []).filter((x) => x.rows);
    }

    renderResults(panel, e, data) {
      const s = this.state;
      this.lastData = { url: this.idxBase + e.f + (e.fv ? '?v=' + e.fv : ''), data };
      const cls = data.cls && data.cls[s.c];
      if (!cls) {
        panel.innerHTML = '<div class="ra-msg"><p>No results for this class in this round.</p></div>';
        return;
      }
      const sess = this.sessionsOf(cls);
      let ses = sess.find((x) => x.k === s.s) || sess[0];
      if (!ses) {
        panel.innerHTML = '<div class="ra-msg"><p>No timing posted for this class yet.</p></div>';
        return;
      }
      s.s = ses.k;
      this.curCls = cls;
      this.curSes = ses.k;
      const alias = (cls.ses || []).filter((x) => x.same).map((x) => x.l + ' matches ' + (x.same === 'ov' ? 'Class' : ((cls.ses.find((y) => y.k === x.same) || {}).l || '')));
      const rows = ses.rows;
      const lead = rows.find((r) => r.p === 1) || rows[0];
      const second = rows.find((r) => r.p === 2);
      const classBest = bestOf(rows);
      const shown = rows.filter((r) => matches(r, s.q));
      const lapsOn = this.tab === 'laps' && ses.k === 'ov';
      const hasG = cls.g != null && cls.g !== '';
      const grpOn = this.tab === 'group' && ses.k === 'ov' && hasG;
      const tabs = sess.map((x) => {
        const btn = `<button type="button" data-ra-s="${esc(x.k)}" aria-pressed="${x.k === ses.k && !lapsOn && !grpOn}">${esc(x.k === 'ov' ? 'Class' : x.l)}</button>`;
        if (x.k !== 'ov') return btn;
        return btn + `<button type="button" data-ra-tab="laps" aria-pressed="${lapsOn}">Laps</button>`
          + (hasG ? `<button type="button" data-ra-tab="group" aria-pressed="${grpOn}">Overall</button>` : '');
      });
      const switcher = sess.length > 1 || sess.some((x) => x.k === 'ov')
        ? `<div class="ra-seg" role="group" aria-label="Session">${tabs.join('')}</div>`
        : '';
      const key = `${e.r}|${s.c}|${ses.k}`;
      if (this.cmp && (lapsOn || grpOn || this.cmp.id.indexOf(key + '|') !== 0)) this.cmp = null;
      this.curRows = rows;
      const motos = rows.some((r) => r.mp);
      const body = lapsOn || grpOn ? '' : shown.map((r) => this.resultRow(r, lead, classBest, key, motos, second)).join('');
      const main = grpOn
        ? this.groupView(e, cls, data, s.q)
        : lapsOn
        ? this.lapsView(rows, shown, s.q)
        : shown.length ? `<div class="ra-tw"><table class="ra-t ra-t--res">
          <thead><tr><th class="c-pos" scope="col">Pos</th><th class="c-num" scope="col">#</th><th class="c-rider" scope="col">Rider</th><th class="c-bike" scope="col">${esc(this.machineLabel(cls.n))}</th><th class="c-laps" scope="col">${motos ? 'Motos' : 'Laps'}</th><th class="c-gap" scope="col">Gap</th><th class="c-time" scope="col">Total time</th><th class="c-best" scope="col">Best lap</th><th class="c-pts" scope="col">Pts</th></tr></thead>
          <tbody>${body}</tbody></table></div>
          <p class="ra-note">${motos ? 'Overall is by points across the motos. Tap a rider for best lap and moto finishes.' : 'Tap a rider for lap by lap times.'}</p>`
        : `<div class="ra-msg"><p>No riders match “${esc(s.q)}”.</p></div>`;
      panel.innerHTML = `
        <div class="ra-ph">
          <div class="ra-ph-main">
            <h2 class="ra-h">${esc(cls.n)}</h2>
            <p class="ra-sub">Round ${esc(e.r)} · ${esc(e.venue)} · ${esc(this.when(e))}${cls.g ? ' · Race group ' + esc(cls.g) : ''} · ${esc(rows.length)} rider${rows.length === 1 ? '' : 's'}</p>
          </div>
          ${switcher}
        </div>
        ${alias.length && sess.length === 1 && !lapsOn && !grpOn ? `<p class="ra-note">${esc(alias.join('. '))}.</p>` : ''}
        ${main}`;
    }

    /* Laps tab: each rider's position and gap to the leader after every lap. */
    lapsView(rows, shown, q) {
      if (!rows.some(hasLaps)) return '<p class="ra-note">No lap times recorded.</p>';
      const laps = lapOrder(rows);
      const idx = new Map(rows.map((r, i) => [r, i]));
      const head = laps.map((x, k) => `<th class="c-lap${x.chg ? ' is-chg' : ''}" scope="col" title="${esc('Lap ' + (k + 1) + ': #' + rows[x.lead].n + ' leads')}">L${esc(k + 1)}</th>`).join('');
      const body = shown.map((r) => {
        const i = idx.get(r);
        const pos = r.p != null ? ordHTML(r.p) : `<span class="ra-st">${esc(r.st || '—')}</span>`;
        const cells = laps.map((x) => {
          if (!x.pos.has(i)) return '<td class="c-lap"></td>';
          const p = x.pos.get(i);
          return `<td class="c-lap${p === 1 ? ' is-lead' : ''}"><b>${ordHTML(p)}</b><small>${p === 1 ? (x.ahead == null ? '—' : esc('-' + lapGap(x.ahead))) : esc('+' + lapGap(x.gap.get(i)))}</small></td>`;
        }).join('');
        return `<tr><th class="c-who" scope="row"><span class="ra-lt-p">${pos}</span><span class="ra-plate">${esc(r.n)}</span><span class="ra-name" title="${esc(r.r)}">${esc(r.r)}</span></th>${cells}</tr>`;
      }).join('');
      return `<div class="ra-laps-v">
        ${shown.length ? `<div class="ra-lw"><table class="ra-lt">
          <thead><tr><th class="c-who" scope="col">Rider</th>${head}</tr></thead>
          <tbody>${body}</tbody></table></div>
          <p class="ra-note">Position after each lap, with the gap to the leader in seconds. The leader's figure is the margin over 2nd. Highlighted laps mark a change of leader.</p>`
        : `<div class="ra-msg"><p>No riders match “${esc(q)}”.</p></div>`}
      </div>`;
    }

    /* Every rider in the round whose class has race group g, from each class's Overall rows.
       Order: finishers, then other statuses, then DNS; each by laps desc, total time asc,
       class name, plate number. Finishers get a group position (gp). */
    groupRows(data, g) {
      const classes = [];
      const rows = [];
      Object.keys(data.cls || {}).forEach((k) => {
        const c = data.cls[k];
        if (!c || c.g == null || String(c.g) !== String(g)) return;
        const cn = c.n || k;
        classes.push({ k, n: cn });
        const ov = (c.ses || []).find((x) => x.k === 'ov' && x.rows);
        if (ov) ov.rows.forEach((r) => rows.push({ r, k, cn }));
      });
      const tier = (r) => (r.p != null ? 0 : String(r.st || '').trim().toUpperCase() === 'DNS' ? 2 : 1);
      const str = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
      const num = (n) => (/^\d+$/.test(String(n)) ? parseInt(n, 10) : Infinity);
      const sec = (v) => (typeof v === 'number' && isFinite(v) ? v : Infinity);
      rows.sort((a, b) => tier(a.r) - tier(b.r)
        || (b.r.l || 0) - (a.r.l || 0)
        || sec(a.r.e) - sec(b.r.e)
        || str(a.cn, b.cn)
        || num(a.r.n) - num(b.r.n)
        || str(String(a.r.n), String(b.r.n)));
      let n = 0;
      rows.forEach((x) => { x.gp = x.r.p != null ? ++n : null; });
      classes.sort((a, b) => str(a.n, b.n));
      return { classes, rows };
    }

    /* Group tab: combined overall for the class's race group. */
    groupView(e, cls, data, q) {
      const g = cls.g;
      const cur = this.state.c;
      const { classes, rows } = this.groupRows(data, g);
      const lead = rows.find((x) => x.gp === 1);
      const second = rows.find((x) => x.gp === 2);
      const gBest = bestOf(rows.map((x) => x.r));
      const shown = rows.filter((x) => matches(x.r, q));
      const hi = classes.some((c) => c.k === this.grpHi) ? this.grpHi : '';
      const chips = `<button type="button" class="ra-gchip" data-ra-ghi="" aria-pressed="${!hi}">All</button>`
        + classes.map((c) => `<button type="button" class="ra-gchip" data-ra-ghi="${esc(c.k)}" aria-pressed="${c.k === hi}">${esc(c.n)}</button>`).join('');
      const body = shown.map((x) => {
        const r = x.r;
        const pos = x.gp != null ? ordHTML(x.gp) : `<span class="ra-st">${esc(r.st || '—')}</span>`;
        let gap = '';
        if (x.gp === 1) gap = esc(leadGap(r, second && second.r, second ? secDiff(second.r.e, r.e) : null));
        else if (x.gp != null && lead) {
          const down = (lead.r.l || 0) - (r.l || 0);
          gap = down > 0 ? `+${esc(down)} lap${down === 1 ? '' : 's'}` : esc(fmtGap(r.e != null && lead.r.e != null ? r.e - lead.r.e : null));
        }
        const lapBest = r.b != null && r.lt ? r.lt[r.b] : null;
        const best = lapBest != null ? lapBest : (r.bl != null ? r.bl : null);
        const isGB = lapBest != null && gBest != null && Math.abs(lapBest - gBest) < 0.0005;
        return `<tr${hi ? (x.k === hi ? ' class="is-hi"' : ' class="is-dim"') : ''}>
          <td class="c-pos">${pos}</td>
          <td class="c-num"><span class="ra-plate">${esc(r.n)}</span></td>
          <th class="c-who" scope="row"><span class="ra-name">${esc(r.r)}</span></th>
          <td class="c-cls">${esc(x.cn)}${r.p != null ? `<small>${esc(r.p + ordSuffix(r.p))} in class</small>` : ''}</td>
          <td class="c-laps">${r.l != null ? esc(r.l) : ''}</td>
          <td class="c-time">${esc(fmtT(r.e))}</td>
          <td class="c-gap">${gap}</td>
          <td class="c-best${isGB ? ' is-cbest' : ''}">${best != null ? esc(fmtT(best)) + (lapBest != null ? `<small>Lap ${esc(r.b + 1)}</small>` : '') : ''}</td>
        </tr>`;
      }).join('');
      const table = !rows.length
        ? '<div class="ra-msg"><p>No timing posted for this group yet.</p></div>'
        : shown.length ? `<div class="ra-lw ra-gw"><table class="ra-gt${hi ? ' has-hi' : ''}">
          <thead><tr><th class="c-pos" scope="col">Pos</th><th class="c-num" scope="col">#</th><th class="c-who" scope="col">Rider</th><th class="c-cls" scope="col">Class</th><th class="c-laps" scope="col">Laps</th><th class="c-time" scope="col">Total time</th><th class="c-gap" scope="col">Gap</th><th class="c-best" scope="col">Best lap</th></tr></thead>
          <tbody>${body}</tbody></table></div>`
        : `<div class="ra-msg"><p>No riders match “${esc(q)}”.</p></div>`;
      return `<div class="ra-grp">
        <h3 class="ra-grp-h">Overall · Group ${esc(g)} · Round ${esc(e.r)}</h3>
        <p class="ra-note">All classes that raced together in group ${esc(g)}, ranked by laps then total time.</p>
        <div class="ra-gchips" role="group" aria-label="Classes in group ${esc(g)}">${chips}</div>
        ${table}
      </div>`;
    }

    resultRow(r, lead, classBest, key, motos, second) {
      const id = 'ra-d-' + (key + '-' + r.n).replace(/[^a-z0-9]+/gi, '-');
      const isOpen = this.open.has(key + '|' + r.n);
      let gap = '—', gapSub = '';
      if (r.p === 1) {
        gap = esc(leadGap(r, second, second ? (second.df != null ? second.df : secDiff(second.e, r.e)) : null));
      } else if (r.p != null) {
        const down = lead && lead.l && r.l != null ? lead.l - r.l : 0;
        gap = down > 0 ? `+${esc(down)} lap${down === 1 ? '' : 's'}` : esc(fmtGap(r.df));
        if (r.gp != null && r.p > 2) gapSub = esc(fmtGap(r.gp)) + ' ahead';
      } else if (r.p == null) {
        gap = '';
      }
      const lapBest = r.b != null && r.lt ? r.lt[r.b] : null;
      const best = lapBest != null ? lapBest : (r.bl != null ? r.bl : null);
      const isCB = lapBest != null && classBest != null && Math.abs(lapBest - classBest) < 0.0005;
      const pod = r.p != null && r.p >= 1 && r.p <= 3;
      const pos = pod ? `<span class="ra-medal"><span>${ordHTML(r.p)}</span></span>` : r.p != null ? ordHTML(r.p) : `<span class="ra-st">${esc(r.st || '—')}</span>`;
      return `
        <tr class="ra-row${pod ? ' is-pod is-p' + r.p : ''}${isOpen ? ' is-open' : ''}" data-ra-row="${esc(key + '|' + r.n)}">
          <td class="c-pos">${pos}</td>
          <td class="c-num"><span class="ra-plate">${esc(r.n)}</span></td>
          <td class="c-rider"><button type="button" class="ra-exp" aria-expanded="${isOpen}" aria-controls="${id}"><span class="ra-plate ra-plate--m">${esc(r.n)}</span><span class="ra-name">${esc(r.r)}</span><span class="ra-loc">${esc(loc(r))}<span class="ra-bike-m">${r.m ? ' · ' + esc(r.m) : ''}</span></span></button></td>
          <td class="c-bike">${esc(r.m || '')}</td>
          <td class="c-laps" data-l="${motos ? 'Motos' : 'Laps'}">${motos ? esc(motoText(r.mp)) : r.l != null ? esc(r.l) : ''}</td>
          <td class="c-gap" data-l="Gap">${gap}${gapSub ? `<small>${gapSub}</small>` : ''}</td>
          <td class="c-time" data-l="Time">${esc(fmtT(r.e))}</td>
          <td class="c-best${isCB ? ' is-cbest' : ''}" data-l="Best">${best != null ? esc(fmtT(best)) + (lapBest != null ? `<small>Lap ${esc(r.b + 1)}</small>` : '') : ''}</td>
          <td class="c-pts" data-l="Pts">${r.pt != null ? ptsHTML(r.pt) : ''}</td>
        </tr>
        <tr class="ra-det" id="${id}"${isOpen ? '' : ' hidden'}><td colspan="9">${isOpen ? this.detail(r, classBest, key + '|' + r.n) : ''}</td></tr>`;
    }

    detail(r, classBest, id) {
      const field = id && this.curSes === 'ov' && this.curRows ? this.curRows.filter((z) => String(z.n) !== String(r.n)) : [];
      const vs = this.cmp && this.cmp.id === id ? field.find((z) => String(z.n) === this.cmp.n) : null;
      const pick = field.length ? this.cmpPick(field, vs, id) : '';
      if (vs) return `<div class="ra-detail ra-detail--vs">${pick}${this.cmpBody(r, vs, classBest)}</div>`;
      const lt = r.lt || [];
      const best = r.b != null ? lt[r.b] : null;
      const facts = [
        ['Total time', fmtT(r.e)],
        ['Average lap', fmtT(r.a)],
        ['Best lap', best != null ? fmtT(best) + ' (lap ' + (r.b + 1) + ')' : (r.bl != null ? fmtT(r.bl) : (lt.length ? '–' : ''))],
        ['Moto finishes', r.mp ? motoText(r.mp) : ''],
        ['Avg speed', r.mph != null && !isNaN(r.mph) ? Number(r.mph).toFixed(2) + ' mph' : ''],
        ['Gap to leader', r.df != null ? fmtGap(r.df) : ''],
        ['Gap to rider ahead', r.gp != null ? fmtGap(r.gp) : ''],
        ['Points', r.pt != null ? r.pt + ' pts' : ''],
        ['Bonus points', r.bo ? r.bo + ' pts' : ''],
        ['Transfer points', r.tp ? r.tp + ' pts' : ''],
        [this.machineLabel(this.curCls && this.curCls.n), r.m || ''],
        ['Hometown', loc(r)],
      ].filter((f) => f[1]);
      const laps = lapItems(r, classBest);
      const ml = !lt.length && this.curCls && this.curSes === 'ov' ? this.motoLaps(this.curCls, r.n) : [];
      const motoHTML = ml.map((m) => `<div class="ra-moto-laps"><h4 class="ra-moto-h">${esc(m.l)}</h4>${lapChart(m.r.lt, m.r.b, m.cb)}<ol class="ra-laps">${lapItems(m.r, m.cb)}</ol></div>`).join('');
      return `<div class="ra-detail">${pick}
        ${lt.length ? `<div class="ra-detail-chart">${lapChart(lt, r.b, classBest)}<p class="ra-legend">${best != null ? '<i class="is-best"></i>Rider best' : ''}${drawsCB(classBest, lt) ? '<i class="is-cbest"></i>Class best' : ''}</p></div>` : ''}
        <dl class="ra-facts">${facts.map((f) => `<div><dt>${esc(f[0])}</dt><dd>${esc(f[1])}</dd></div>`).join('')}</dl>
        ${laps ? `<ol class="ra-laps">${laps}</ol>` : motoHTML || '<p class="ra-note">No lap times recorded.</p>'}
      </div>`;
    }

    /* Compare picker for an open row: the other riders of the session, in result order. */
    cmpPick(field, vs, id) {
      const pos = (z) => (z.p != null ? z.p + ordSuffix(z.p) : (z.st || '–'));
      const opts = `<option value=""${vs ? '' : ' selected'}>Choose a rider</option>`
        + field.map((z) => `<option value="${esc(z.n)}"${z === vs ? ' selected' : ''}>${esc(pos(z))} · #${esc(z.n)} ${esc(z.r)}</option>`).join('');
      return `<div class="ra-vs-pick ra-cmp-pick"><label>Compare with <select data-ra-cmp="${esc(id)}">${opts}</select></label>${vs ? `<button type="button" class="ra-cmp-x" data-ra-cmpx="${esc(id)}"><span aria-hidden="true">&times;</span> Clear</button>` : ''}</div>`;
    }

    /* Two riders of one session side by side: lap chart, summary figures and lap-by-lap differences (a minus b). */
    cmpBody(a, b, classBest) {
      const rows = this.curRows || [];
      const lead = rows.find((z) => z.p === 1);
      const posH = (z) => (z.p != null ? ordHTML(z.p) : `<span class="ra-st">${esc(z.st || '—')}</span>`);
      const gap = (z) => {
        if (z.p === 1) return 'Leader';
        if (z.p == null) return '—';
        const down = lead && lead.l != null && z.l != null ? lead.l - z.l : 0;
        if (down > 0) return `+${down} lap${down === 1 ? '' : 's'}`;
        return z.df != null ? fmtGap(z.df) : '—';
      };
      const bestT = (z) => (z.b != null && z.lt && typeof z.lt[z.b] === 'number' ? fmtT(z.lt[z.b]) + ' (lap ' + (z.b + 1) + ')' : (z.bl != null ? fmtT(z.bl) : '—'));
      const mph = (z) => (z.mph != null && !isNaN(z.mph) ? Number(z.mph).toFixed(2) + ' mph' : '—');
      const txt = (v) => esc(v === '' || v == null ? '—' : v);
      const facts = [
        ['Position', posH(a), posH(b)],
        ['Laps', txt(a.l), txt(b.l)],
        ['Total time', txt(fmtT(a.e)), txt(fmtT(b.e))],
        ['Gap to leader', txt(gap(a)), txt(gap(b))],
        ['Best lap', txt(bestT(a)), txt(bestT(b))],
        ['Average lap', txt(fmtT(a.a)), txt(fmtT(b.a))],
        ['Avg speed', txt(mph(a)), txt(mph(b))],
      ];
      const nameTh = (z, cls) => `<th scope="col" class="${cls}"><span class="ra-plate">${esc(z.n)}</span><span class="ra-cmp-nm">${esc(z.r)}</span></th>`;
      const stats = `<div class="ra-cmp-tw ra-cmp-stats"><table class="ra-cmp-t">
        <thead><tr><th scope="col"><span class="visually-hidden">Figure</span></th>${nameTh(a, 'is-a')}${nameTh(b, 'is-b')}</tr></thead>
        <tbody>${facts.map((f) => `<tr><th scope="row">${esc(f[0])}</th><td>${f[1]}</td><td>${f[2]}</td></tr>`).join('')}</tbody></table></div>`;
      const ok = (v) => typeof v === 'number' && isFinite(v) && v > 0;
      const la = hasLaps(a) ? a.lt : [], lb = hasLaps(b) ? b.lt : [];
      let fa = 0, fb = 0, both = 0;
      let body = '';
      if (la.length && lb.length) {
        const n = Math.max(la.length, lb.length);
        for (let i = 0; i < n; i++) {
          const va = ok(la[i]) ? la[i] : null, vb = ok(lb[i]) ? lb[i] : null;
          const d = va != null && vb != null ? (Math.round(va * 1000) - Math.round(vb * 1000)) / 1000 : null;
          if (d != null) { both++; if (d < 0) fa++; else if (d > 0) fb++; }
          const cell = (v, fast) => `<td class="${fast ? 'is-fast' : ''}">${v != null ? esc(fmtT(v)) : '—'}</td>`;
          body += `<tr><th scope="row">Lap ${esc(i + 1)}</th>${cell(va, d != null && d < 0)}${cell(vb, d != null && d > 0)}<td class="ra-cmp-d">${d != null ? esc(fmtDiff(d)) : '—'}</td></tr>`;
        }
      }
      const head = `<div class="ra-vs-head ra-cmp-head">
          <div class="ra-vs-p"><span class="ra-plate">${esc(a.n)}</span><b>${esc(a.r)}</b><span>${posH(a)}</span></div>
          <div class="ra-vs-mid"><span class="ra-vs-x">VS</span>${both ? `<span class="ra-vs-score"><b>${esc(fa)}</b>–<b>${esc(fb)}</b></span><small>faster laps of ${esc(both)}</small>` : ''}</div>
          <div class="ra-vs-p ra-vs-p--b"><span class="ra-plate">${esc(b.n)}</span><b>${esc(b.r)}</b><span>${posH(b)}</span></div>
        </div>`;
      if (!body) {
        const none = [a, b].filter((z) => !hasLaps(z)).map((z) => z.r);
        return head + stats + `<p class="ra-note ra-cmp-note">No lap times recorded for ${esc(none.join(' and '))}, so laps cannot be compared.</p>`;
      }
      const chart = `<div class="ra-detail-chart ra-cmp-chart">${lapChart2(la, a.b, lb, b.b, classBest, '#' + a.n, '#' + b.n)}
          <p class="ra-legend ra-cmp-key"><span><i class="is-a"></i>${esc(a.r)}</span><span><i class="is-b"></i>${esc(b.r)}</span>${a.b > 0 || b.b > 0 ? '<span><i class="is-best"></i>Best lap</span>' : ''}${drawsCB(classBest, la, lb) ? '<span><i class="is-cbest"></i>Class best</span>' : ''}</p></div>`;
      const laps = `<div class="ra-cmp-tw ra-cmp-laps"><table class="ra-cmp-t">
        <thead><tr><th scope="col">Lap</th>${nameTh(a, 'is-a')}${nameTh(b, 'is-b')}<th scope="col">Difference</th></tr></thead>
        <tbody>${body}</tbody></table></div>
        <p class="ra-note ra-cmp-note">Difference is ${esc(a.r)} minus ${esc(b.r)}; a minus sign means ${esc(a.r)} was faster. The faster lap of each pair is in green.</p>`;
      return head + chart + stats + laps;
    }

    /* Redraw an open row's detail panel after a compare change. */
    redrawDetail(id, focus) {
      const tr = Array.from(this.root.querySelectorAll('tr.ra-row[data-ra-row]')).find((x) => x.dataset.raRow === id);
      const det = tr && tr.nextElementSibling;
      if (!det || !det.classList.contains('ra-det')) return;
      const r = this.rowData(id);
      if (!r || !r.row) return;
      det.firstElementChild.innerHTML = this.detail(r.row, r.classBest, id);
      const sel = focus && det.querySelector('[data-ra-cmp]');
      if (sel) sel.focus();
    }

    /* Per-moto lap rows for one rider, for classes whose overall has no lap data. */
    motoLaps(cls, n) {
      return this.sessionsOf(cls).filter((x) => x.k !== 'ov').map((x) => {
        const r = x.rows.find((z) => String(z.n) === String(n));
        return hasLaps(r) ? { l: x.l, r, cb: bestOf(x.rows) } : null;
      }).filter(Boolean);
    }

    /* Machine column label: TX4 classes are ATV or UTV; other orgs race bikes. */
    machineLabel(cn) {
      if (String((this.idx && this.idx.org) || '').toLowerCase() !== 'tx4') return 'Bike';
      return /utv/i.test(String(cn || '')) ? 'UTV' : 'ATV';
    }

    /* Season drop rule as a count: 2, 1, or 0 when the season has none. */
    ruleN() {
      const r = this.st && this.st.rule;
      return r === 'drop2' ? 2 : r === 'drop1' ? 1 : 0;
    }

    /* Drop count in use: the chosen one when the season has a rule, else 0. */
    dropN() {
      const rule = this.ruleN();
      if (!rule) return 0;
      const d = this.state.dr;
      return d === 0 || d === 1 || d === 2 ? d : rule;
    }

    dropSeg() {
      const k = this.dropN(), rule = this.ruleN();
      return `<div class="ra-seg ra-drops" role="group" aria-label="Dropped rounds">${DROP_OPTS.map(([v, l]) => `<button type="button" data-ra-dr="${v}" aria-pressed="${v === k}"${v === rule ? ' title="Series rule"' : ''}>${esc(l)}</button>`).join('')}</div>`;
    }

    dropNote(k) {
      if (!k) return 'Every round counted. Ranks use full totals.';
      const txt = k === 1
        ? "Struck-through rounds are each rider's lowest. They are dropped and do not count toward the total."
        : `Struck-through rounds are each rider's ${esc(k)} lowest. They are dropped and do not count toward the total.`;
      return `<span class="ra-dropkey" aria-hidden="true">12th<small>8 pts</small></span><span>${txt}</span>`;
    }

    /* Apply a drop count, keep it for later visits, and redraw the table and any open rider card. */
    setDrop(k, inPop) {
      saveDrop(k);
      this.state.dr = k;
      this.writeURL(false);
      this.render();
      if (this.pop && !this.pop.hidden) this.fillRider();
      const scope = inPop ? this.pop : this.root;
      const b = scope && scope.querySelector(`[data-ra-dr="${k}"]`);
      if (b) b.focus();
    }

    renderStandings(panel) {
      const s = this.state;
      const c = this.st.cls[s.c];
      if (!c) {
        panel.innerHTML = '<div class="ra-msg"><p>No standings for this class yet.</p></div>';
        return;
      }
      const evByR = {};
      this.idx.ev.forEach((e) => { evByR[e.r] = e; });
      const rule = this.ruleN();
      const k = this.dropN();
      const sd = standingsFor(c, k);
      const rows = sd.rows;
      const total = (r) => sd.info.get(r).total;
      const top = rows.length ? total(rows[0]) : 0;
      const ranked = rows.map((r) => ({ r, pos: sd.info.get(r).pos }));
      const shown = ranked.filter((x) => matches(x.r, s.q));
      const sched = this.schedState();
      const future = sched.filter((x) => x.st !== 'done' && !c.rd.includes(x.n));
      const heads = c.rd.map((n) => {
        const e = evByR[n];
        return `<th class="c-rd is-done" scope="col"${e ? ` title="${esc(e.venue + ' · ' + this.when(e))}"` : ''}>R${n == null ? '?' : esc(n)}</th>`;
      }).join('') + future.map((x) => `<th class="c-rd is-${esc(x.st)}" scope="col" title="${esc((x.st === 'next' ? 'Next up' : 'Upcoming') + (x.s ? ' · ' + fmtSpan(x) : ''))}">R${esc(x.n)}</th>`).join('');
      const futureCells = future.map((x) => `<td class="c-rd is-${esc(x.st)}" data-l="R${esc(x.n)}"><b class="ra-dash">${x.st === 'next' ? 'Next' : '·'}</b><small>&nbsp;</small></td>`).join('');
      const body = shown.map(({ r, pos }) => {
        const cells = (r.rd || []).map((cell, i) => {
          const dropped = sd.info.get(r).x.includes(i);
          const [p, pts, st] = cell;
          const label = `R${c.rd[i] == null ? '?' : c.rd[i]}`;
          const inner = p != null ? `<b>${ordHTML(p)}</b><small>${ptsHTML(pts)}</small>` : `<b class="ra-dash">${esc(st && st !== '0' && st !== '--' ? st : '–')}</b><small>${ptsHTML(pts || 0)}</small>`;
          return `<td class="c-rd is-done${dropped ? ' is-drop' : ''}${p === 1 ? ' is-win' : ''}" data-l="${esc(label)}"${dropped ? ' title="Dropped round, not counted in the total"' : ''}>${inner}</td>`;
        }).join('') + futureCells;
        const gap = esc(total(r) === top ? '—' : '-' + (top - total(r)));
        return `<tr class="ra-row ra-srow${pos != null && pos <= 3 ? ' is-top' : ''}">
          <td class="c-pos">${pos != null ? ordHTML(pos) : ''}</td>
          <td class="c-num"><span class="ra-plate">${esc(r.n)}</span></td>
          <td class="c-rider"><button type="button" class="ra-who" data-ra-rider="${esc(slugOf(r))}" aria-haspopup="dialog"><span class="ra-plate ra-plate--m">${esc(r.n)}</span><span class="ra-name">${esc(r.r)}</span><span class="ra-loc">${esc(loc(r))}<span class="ra-bike-m">${r.m ? ' · ' + esc(r.m) : ''}</span></span></button></td>
          <td class="c-bike">${esc(r.m || '')}</td>
          <td class="c-pts"><b>${ptsHTML(total(r))}</b><small class="ra-gap-m">${gap}</small></td>
          <td class="c-gap">${gap}</td>
          ${cells}
        </tr>`;
      }).join('');
      panel.innerHTML = `
        <div class="ra-ph">
          <div class="ra-ph-main">
            <h2 class="ra-h">${esc(c.n)}</h2>
            <p class="ra-sub">${esc(this.idx.season)} series points · ${esc(c.rows.length)} rider${c.rows.length === 1 ? '' : 's'} · after ${esc(c.rd.length)} round${c.rd.length === 1 ? '' : 's'}</p>
          </div>
          ${rule ? this.dropSeg() : ''}
        </div>
        ${rule ? `<p class="ra-note ra-dropnote">${this.dropNote(k)}</p>` : ''}
        ${shown.length ? `<div class="ra-tw ra-tw--st"><table class="ra-t ra-t--st">
          <thead><tr><th class="c-pos" scope="col">Pos</th><th class="c-num" scope="col">#</th><th class="c-rider" scope="col">Rider</th><th class="c-bike" scope="col">${esc(this.machineLabel(c.n))}</th><th class="c-pts" scope="col">Pts</th><th class="c-gap" scope="col">Gap</th>${heads}</tr></thead>
          <tbody>${body}</tbody></table></div>
          <p class="ra-note">Tap a rider for round by round results.</p>`
        : `<div class="ra-msg"><p>No riders match “${esc(s.q)}”.</p></div>`}`;
    }

    toggleRow(tr) {
      const id = tr.dataset.raRow;
      const det = tr.nextElementSibling;
      const btn = tr.querySelector('.ra-exp');
      const open = !this.open.has(id);
      if (open) this.open.add(id); else this.open.delete(id);
      const hadCmp = !open && this.cmp && this.cmp.id === id;
      if (hadCmp) this.cmp = null;
      tr.classList.toggle('is-open', open);
      if (btn) btn.setAttribute('aria-expanded', String(open));
      if (det && det.classList.contains('ra-det')) {
        if (open && !det.firstElementChild.innerHTML) {
          const r = this.rowData(id);
          if (r) det.firstElementChild.innerHTML = this.detail(r.row, r.classBest, id);
        }
        if (hadCmp) det.firstElementChild.innerHTML = '';
        det.hidden = !open;
      }
    }

    rowData(id) {
      const [rnd, c, sk, n] = id.split('|');
      const e = this.idx.ev.find((x) => String(x.r) === rnd);
      if (!e) return null;
      const url = this.idxBase + e.f + (e.fv ? '?v=' + e.fv : '');
      if (!this.lastData || this.lastData.url !== url) return null;
      const cls = this.lastData.data.cls[c];
      const ses = cls && cls.ses.find((x) => x.k === sk);
      if (!ses) return null;
      return { row: ses.rows.find((r) => String(r.n) === n), classBest: bestOf(ses.rows) };
    }

    go(patch, push) {
      Object.assign(this.state, patch);
      this.writeURL(push !== false);
      this.render();
    }

    onClick(e) {
      const t = e.target.closest('button, tr.ra-row[data-ra-row], [data-ra-go]');
      if (!t || !this.root.contains(t)) return;
      const d = t.dataset;
      if (d.raCmpx != null) { this.cmp = null; return this.redrawDetail(d.raCmpx, true); }
      if (d.raDr != null) return this.setDrop(Number(d.raDr), false);
      if (d.raRider) return this.openRider(d.raRider, t);
      if (d.raGo) return this.go({ r: parseInt(d.raGo, 10), v: 'results', s: '' }), this.scrollToBar();
      if (d.raV) return this.go({ v: d.raV, s: '' });
      if (d.raR) return this.go({ r: parseInt(d.raR, 10), s: '' });
      if (d.raG) {
        const first = this.classList().find((c) => c.grp === d.raG);
        if (first) this.go({ c: first.k, s: '' });
        return;
      }
      if (d.raC) return this.go({ c: d.raC, s: '' });
      if (d.raGhi != null) { this.grpHi = d.raGhi; return this.go({}, false); }
      if (d.raTab) { this.tab = d.raTab; this.grpHi = ''; return this.go({ s: 'ov' }); }
      if (d.raS) { this.tab = ''; return this.go({ s: d.raS }); }
      const tr = t.matches('tr') ? t : t.closest('tr.ra-row[data-ra-row]');
      if (tr && tr.dataset.raRow) this.toggleRow(tr);
    }

    onKey(e) {
      if (e.key !== 'Escape') return;
      const qi = e.target.closest('[data-ra-q]');
      if (qi && qi.value) { qi.value = ''; this.onInput({ target: qi }); }
    }

    onInput(e) {
      if (!e.target.matches('[data-ra-q]')) return;
      clearTimeout(this.qT);
      this.qT = setTimeout(() => {
        this.state.q = e.target.value.trim();
        this.writeURL(false);
        this.render();
      }, 160);
    }

    onChange(e) {
      if (e.target.matches('[data-ra-cmp]')) {
        const id = e.target.dataset.raCmp;
        this.cmp = e.target.value ? { id, n: e.target.value } : null;
        this.redrawDetail(id, true);
        return;
      }
      if (e.target.matches('[data-ra-season]')) {
        this.state = { y: e.target.value, r: null, c: this.state.c, v: this.state.v, s: '', dr: readDrop(), q: '' };
        this.writeURL(true);
        this.load();
      }
    }

    /* Rider popup: round by round view of one rider's standings row. */
    findRider(slug) {
      if (!this.st || !slug) return null;
      const hit = (k) => {
        const c = this.st.cls[k];
        const r = c && (c.rows.find((x) => slugOf(x) === slug) || c.rows.find((x) => oldSlugOf(x) === slug));
        return r ? { k, c, r } : null;
      };
      return hit(this.state.c) || Object.keys(this.st.cls).reduce((a, k) => a || hit(k), null);
    }

    setHash(slug, rd) {
      const h = hashParams();
      h.delete('standings');
      if (slug) h.set('rider', slug); else h.delete('rider');
      if (slug && rd != null) h.set('r', rd); else h.delete('r');
      const qs = h.toString().replace(/=(&|$)/g, '$1');
      const url = location.pathname + location.search + (qs ? '#' + qs : '');
      if (url !== location.pathname + location.search + location.hash) history.replaceState(null, '', url);
    }

    syncRider() {
      const h = hashParams();
      const slug = h.get('rider');
      if (!slug) { if (this.pop && !this.pop.hidden) this.closeRider(true); return; }
      if (this.state.v !== 'standings' || !this.st) return;
      const rd = h.has('r') ? Number(h.get('r')) : null;
      const open = this.pop && !this.pop.hidden && this.popSlug === slug;
      if (open && (this.popMode === 'round' ? this.popRd : null) === rd) return;
      if (open) { this.showRound(rd); return; }
      this.openRider(slug, null, rd);
    }

    popShell() {
      if (this.pop) return this.pop;
      const el = document.createElement('div');
      el.className = 'ra-pop';
      el.hidden = true;
      el.innerHTML = `<div class="ra-pop-bg" data-ra-close></div>
        <div class="ra-pop-box" role="dialog" aria-modal="true" aria-labelledby="ra-pop-h-${esc(this.el.id)}" tabindex="-1">
          <button type="button" class="ra-pop-x" data-ra-close aria-label="Close"><span aria-hidden="true">&times;</span></button>
          <div class="ra-pop-in" data-ra-pop-in></div>
        </div>`;
      el.addEventListener('click', (e) => {
        if (e.target.closest('[data-ra-close]')) { this.closeRider(); return; }
        const m = e.target.closest('[data-ra-mode]');
        if (m) {
          const from = this.popMode === 'round' ? this.popRd : null;
          if (from != null) { this.showRound(null); return; }
          this.popMode = m.dataset.raMode === 'vs' ? 'vs' : 'card';
          this.fillRider();
          this.focusMode();
          return;
        }
        const dr = e.target.closest('[data-ra-dr]');
        if (dr) { this.setDrop(Number(dr.dataset.raDr), true); return; }
        const rr = e.target.closest('[data-ra-rd]');
        if (rr) { this.showRound(Number(rr.dataset.raRd)); return; }
        const b = e.target.closest('[data-ra-rider]');
        if (b) this.openRider(b.dataset.raRider, null);
      });
      el.addEventListener('change', (e) => {
        if (e.target.matches('[data-ra-vs]')) { this.popVs = e.target.value; this.fillRider(); const sel = el.querySelector('[data-ra-vs]'); if (sel) sel.focus(); }
      });
      el.addEventListener('keydown', (e) => this.popKey(e));
      let rt = 0;
      window.addEventListener('resize', () => {
        clearTimeout(rt);
        rt = setTimeout(() => {
          const w0 = this.popW;
          if (el.hidden || this.popMode !== 'round' || this.chartW() === w0) return;
          const had = el.contains(document.activeElement);
          this.fillRider();
          if (had && !el.contains(document.activeElement)) this.focusMode();
        }, 150);
      });
      this.el.appendChild(el);
      this.pop = el;
      return el;
    }

    popKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); this.closeRider(); return; }
      if (e.key !== 'Tab') return;
      const box = this.pop.querySelector('.ra-pop-box');
      const f = Array.from(box.querySelectorAll('button, [href], select, input, [tabindex]:not([tabindex="-1"])')).filter((x) => !x.disabled && x.offsetParent !== null);
      if (!f.length) { e.preventDefault(); box.focus(); return; }
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === box)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /* Switch between the race card and one round's lap view; null returns to the card. */
    showRound(rd) {
      const from = this.popMode === 'round' ? this.popRd : null;
      const ok = rd != null && this.popHit && this.popHit.c.rd.includes(rd);
      this.popMode = ok ? 'round' : 'card';
      this.popRd = ok ? rd : null;
      this.setHash(this.popSlug, this.popRd);
      this.fillRider();
      const box = this.pop.querySelector('.ra-pop-box');
      if (box) box.scrollTop = 0;
      if (!ok && from != null) {
        const b = this.pop.querySelector(`button[data-ra-rd="${from}"]`);
        if (b) { b.focus(); return; }
      }
      this.focusMode();
    }

    openRider(slug, opener, rd) {
      const hit = this.findRider(slug);
      if (!hit) { this.setHash(''); return; }
      slug = slugOf(hit.r);
      const el = this.popShell();
      const wasOpen = !el.hidden;
      if (opener) this.popOpener = opener;
      if (this.popSlug !== slug) { this.popVs = ''; this.popMode = 'card'; }
      this.popSlug = slug;
      this.popHit = hit;
      const okRd = rd != null && hit.c.rd.includes(rd);
      if (okRd) { this.popMode = 'round'; this.popRd = rd; } else if (this.popMode === 'round') { this.popMode = 'card'; this.popRd = null; }
      this.setHash(slug, okRd ? rd : null);
      el.className = 'ra-pop';
      el.hidden = false;
      document.documentElement.classList.add('ra-noscroll');
      this.fillRider();
      if (!wasOpen) requestAnimationFrame(() => el.querySelector('.ra-pop-x').focus());
      else this.focusMode();
      this.loadRounds(hit).then(() => {
        if (this.popHit !== hit || el.hidden) return;
        const had = el.contains(document.activeElement) ? document.activeElement : null;
        const keep = had && had.matches('.ra-pop-x') ? had : null;
        this.fillRider();
        if (had && !keep && !el.contains(document.activeElement)) this.focusMode();
      });
    }

    focusMode() {
      const b = this.pop && this.pop.querySelector('[data-ra-mode]');
      if (b) b.focus();
    }

    closeRider(fromHash) {
      if (!this.pop || this.pop.hidden) return;
      this.pop.hidden = true;
      const was = this.popSlug;
      this.popSlug = '';
      this.popHit = null;
      document.documentElement.classList.remove('ra-noscroll');
      if (!fromHash) this.setHash('');
      const back = this.popOpener && document.contains(this.popOpener) ? this.popOpener
        : (was && this.root.querySelector(`[data-ra-rider="${CSS.escape(was)}"]`)) || this.root.querySelector('[data-ra-rider]');
      if (back) back.focus();
    }

    /* Per-round session rows from the cached round files, keyed by round number. */
    async loadRounds(hit) {
      this.rdRows = this.rdRows || {};
      this.rdMoto = this.rdMoto || {};
      const jobs = hit.c.rd.map(async (n) => {
        const key = n + '|' + hit.k;
        if (this.rdRows[key] !== undefined) return;
        const e = this.idx.ev.find((x) => x.r === n);
        if (!e) { this.rdRows[key] = null; return; }
        try {
          const data = await getJSON(this.idxBase + e.f + (e.fv ? '?v=' + e.fv : ''));
          const cls = data.cls && data.cls[hit.k];
          const sess = cls ? this.sessionsOf(cls) : [];
          const ses = sess.find((x) => x.k === 'ov') || sess[0];
          this.rdRows[key] = ses ? ses.rows : null;
          this.rdMoto[key] = sess.filter((x) => x !== ses && x.k !== 'ov').map((x) => ({ l: x.l, rows: x.rows }));
        } catch (err) {
          this.rdRows[key] = null;
        }
      });
      await Promise.all(jobs);
    }

    /* Round by round facts for one rider; lap fields stay empty until round files load. */
    riderRounds(hit, r) {
      const evByR = {};
      this.idx.ev.forEach((e) => { evByR[e.r] = e; });
      const x = standingsFor(hit.c, this.dropN()).info.get(r).x;
      const done = hit.c.rd.map((n, i) => {
        const cell = (r.rd || [])[i] || [];
        const rows = this.rdRows ? this.rdRows[n + '|' + hit.k] : undefined;
        const row = rows ? rows.find((x) => String(x.n) === String(r.n)) : null;
        const lead = rows ? rows.find((x) => x.p === 1) : null;
        let gap = '';
        if (row && row.p === 1) gap = 'Leader';
        else if (row && row.p != null) {
          const down = lead && lead.l != null && row.l != null ? lead.l - row.l : 0;
          gap = down > 0 ? `+${down} lap${down === 1 ? '' : 's'}` : fmtGap(row.df);
        }
        const st = cell[2] && cell[2] !== '0' && cell[2] !== '--' ? cell[2] : '';
        return {
          n, ev: evByR[n] || {}, state: 'done',
          p: cell[0], pts: cell[1] || 0, st,
          drop: x.includes(i),
          best: row && row.b != null && row.lt ? fmtT(row.lt[row.b]) : (row && row.bl != null ? fmtT(row.bl) : ''),
          gap, loaded: rows !== undefined,
        };
      });
      const future = this.schedState().filter((x) => x.st !== 'done' && !hit.c.rd.includes(x.n))
        .map((x) => ({ n: x.n, ev: { r: x.n, d: x.s }, state: x.st, p: null, pts: null, st: '', drop: false, best: '', gap: '', loaded: true }));
      return done.concat(future);
    }

    riderStats(rounds, r, c) {
      const fin = rounds.filter((x) => x.state === 'done' && x.p != null);
      const wins = fin.filter((x) => x.p === 1).length;
      const pods = fin.filter((x) => x.p <= 3).length;
      const avg = fin.length ? (fin.reduce((a, x) => a + x.p, 0) / fin.length) : null;
      const bestP = fin.length ? Math.min.apply(null, fin.map((x) => x.p)) : null;
      const top = fin.slice().sort((a, b) => b.pts - a.pts || a.p - b.p)[0];
      const o = standingsFor(c, this.dropN()).info.get(r);
      return {
        wins, pods,
        avg: avg != null ? avg.toFixed(1) : '–',
        bestP, top,
        pos: o.pos,
        total: o.total,
      };
    }

    finishHTML(x) {
      if (x.state !== 'done') return `<span class="ra-fin ra-fin--${esc(x.state)}">${x.state === 'next' ? 'Next' : '–'}</span>`;
      if (x.p == null) return `<span class="ra-fin ra-fin--st">${esc(x.st || '–')}</span>`;
      return `<span class="ra-fin${x.p <= 3 ? ' ra-fin--' + esc(x.p) : ''}">${ordHTML(x.p)}</span>`;
    }

    roundLabel(x) {
      const v = x.ev.venue ? esc(x.ev.venue) : (x.state === 'next' ? 'Next up' : 'Upcoming');
      return `<b>R${esc(x.n)}</b><span>${v}</span><small>${esc(this.when(x.ev))}</small>`;
    }

    fillRider() {
      const hit = this.popHit;
      if (!hit || !this.pop) return;
      const r = hit.r;
      const rounds = this.riderRounds(hit, r);
      const stats = this.riderStats(rounds, r, hit.c);
      const hid = 'ra-pop-h-' + esc(this.el.id);
      const head = `
        <header class="ra-rc-head">
          <span class="ra-plate ra-rc-plate">${esc(r.n)}</span>
          <div class="ra-rc-who">
            <p class="ra-rc-k">${esc(hit.c.n)} · ${esc(this.idx.season)}</p>
            <h2 class="ra-rc-h" id="${hid}">${esc(r.r)}</h2>
            <p class="ra-rc-meta">${[r.m, loc(r)].filter(Boolean).map(esc).join(' · ')}</p>
          </div>
          <div class="ra-rc-rank"><b>${stats.pos != null ? ordHTML(stats.pos) : '–'}</b><span>${ptsHTML(stats.total)}</span></div>
        </header>`;
      const tiles = [
        ['Wins', esc(stats.wins)],
        ['Podiums', esc(stats.pods)],
        ['Avg finish', esc(stats.avg)],
        ['Best finish', stats.bestP != null ? ordHTML(stats.bestP) : '–'],
      ];
      const statRow = `<dl class="ra-rc-stats">${tiles.map((t) => `<div><dt>${esc(t[0])}</dt><dd>${t[1]}</dd></div>`).join('')}</dl>`;
      const dropNote = standingsFor(hit.c, this.dropN()).info.get(r).x.length ? `<p class="ra-rc-note"><span class="ra-tag-drop">Dropped</span> rounds do not count toward the total.</p>` : '';
      const many = hit.c.rows.length > 1;
      const seg = this.ruleN() ? `<div class="ra-rc-drops">${this.dropSeg()}</div>` : '';
      if (this.popMode === 'round') {
        const x = rounds.find((z) => z.n === this.popRd);
        if (x) {
          this.pop.querySelector('[data-ra-pop-in]').innerHTML = `<div class="ra-rc-acts"><button type="button" class="ra-rc-btn ra-rc-btn--back" data-ra-mode="card"><span aria-hidden="true">&lsaquo;</span> Back</button></div>` + head + this.popRound(hit, x);
          return;
        }
      }
      const body = this.popMode === 'vs' && many
        ? `<div class="ra-rc-acts"><button type="button" class="ra-rc-btn ra-rc-btn--back" data-ra-mode="card"><span aria-hidden="true">&lsaquo;</span> Race card</button></div>` + head + seg + this.popVersus(hit, rounds)
        : head + seg + `<div class="ra-rc-acts">${many ? `<button type="button" class="ra-rc-btn" data-ra-mode="vs">Compare <span aria-hidden="true">&rsaquo;</span></button>` : ''}</div>` + statRow + this.popChart(rounds) + this.popTable(rounds) + dropNote;
      this.pop.querySelector('[data-ra-pop-in]').innerHTML = body;
    }

    popChart(rounds) {
      const W = 600, H = 150, T = 24, B = 24, n = rounds.length;
      if (!n) return '';
      const maxP = Math.max(25, ...rounds.map((x) => x.pts || 0));
      const slot = W / n, bw = Math.min(46, slot * 0.62);
      const bars = rounds.map((x, i) => {
        const cx = slot * i + slot / 2;
        const h = x.state === 'done' ? Math.max(2, ((x.pts || 0) / maxP) * (H - T - B)) : H - T - B;
        const y = H - B - h;
        const cls = x.state !== 'done' ? 'is-' + x.state : (x.drop ? 'is-drop' : (x.p === 1 ? 'is-win' : ''));
        const top = x.state === 'done' ? (x.p != null ? x.p + ordSuffix(x.p) : (x.st || '–')) : (x.state === 'next' ? 'Next' : '');
        return `<g class="${esc(cls)}"><rect${x.drop ? ` style="fill:url(#ra-hatch-${esc(this.el.id)})"` : ''} x="${(cx - bw / 2).toFixed(1)}" y="${y.toFixed(1)}" width="${bw.toFixed(1)}" height="${h.toFixed(1)}"/>`
          + `<text class="ra-ch-top" x="${cx.toFixed(1)}" y="${(y - 6).toFixed(1)}">${esc(top)}</text>`
          + (x.state === 'done' ? `<text class="ra-ch-pts" x="${cx.toFixed(1)}" y="${(H - B - 6).toFixed(1)}">${esc(x.pts)}</text>` : '')
          + `<text class="ra-ch-x" x="${cx.toFixed(1)}" y="${H - 6}">R${esc(x.n)}</text></g>`;
      }).join('');
      return `<figure class="ra-rc-chart"><svg viewBox="0 0 ${W} ${H}" role="img" aria-label="Points by round">
        <defs><pattern id="ra-hatch-${esc(this.el.id)}" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="6" height="6" class="ra-hatch-bg"/><line x1="0" y1="0" x2="0" y2="6" class="ra-hatch-ln"/></pattern></defs>
        ${bars}</svg><figcaption>Points by round</figcaption></figure>`;
    }

    popTable(rounds) {
      return `<div class="ra-rc-tw"><table class="ra-rc-t">
        <thead><tr><th scope="col">Round</th><th scope="col">Finish</th><th scope="col">Pts</th><th scope="col">Best lap</th><th scope="col">Gap</th></tr></thead>
        <tbody>${rounds.map((x) => `<tr class="is-${esc(x.state)}${x.drop ? ' is-drop' : ''}${x.state === 'done' ? ' is-link' : ''}"${x.state === 'done' ? ` data-ra-rd="${esc(x.n)}"` : ''}>
          <td class="ra-rc-rd">${x.state === 'done' ? `<button type="button" class="ra-rc-go" data-ra-rd="${esc(x.n)}" aria-label="Round ${esc(x.n)} laps">${this.roundLabel(x)}</button>` : this.roundLabel(x)}${x.drop ? '<span class="ra-tag-drop">Dropped</span>' : ''}</td>
          <td>${this.finishHTML(x)}</td>
          <td class="ra-rc-pts">${x.state === 'done' ? ptsHTML(x.pts) : ''}</td>
          <td data-l="Best lap">${x.state === 'done' ? (x.loaded ? esc(x.best || '–') : '<span class="ra-skel-i"></span>') : ''}</td>
          <td data-l="Gap">${x.state === 'done' ? (x.loaded ? esc(x.gap || '–') : '<span class="ra-skel-i"></span>') : ''}</td>
        </tr>`).join('')}</tbody></table></div>`;
    }

    /* One round for the open rider: lap list, lap-time chart, position by lap and summary figures. */
    popRound(hit, x) {
      const key = x.n + '|' + hit.k;
      const rows = this.rdRows ? this.rdRows[key] : undefined;
      const title = `<div class="ra-rd-title"><b>R${esc(x.n)}</b><span>${esc(x.ev.venue || '')}</span><small>${esc(this.when(x.ev, true))}${x.ev.city ? ' · ' + esc(x.ev.city) : ''}</small></div>`;
      if (rows === undefined) return title + '<div class="ra-rd-load"><span class="ra-skel-i"></span><span class="ra-skel-i"></span><span class="ra-skel-i"></span></div>';
      const me = rows ? rows.find((z) => String(z.n) === String(hit.r.n)) : null;
      const fin = `<div class="ra-rd-fin">${this.finishHTML(x)}<span>${ptsHTML(x.pts)}</span></div>`;
      const foot = '<p class="ra-rc-note">Lap 1 includes the start. Consistency is the spread between the best and median lap.</p>';
      if (hasLaps(me)) return title + this.roundLaps(hit, x, rows, me, true) + foot;
      const motos = ((this.rdMoto && this.rdMoto[key]) || [])
        .map((m) => ({ l: m.l, rows: m.rows, me: m.rows.find((z) => String(z.n) === String(hit.r.n)) }))
        .filter((m) => hasLaps(m.me));
      if (!motos.length) return title + fin + '<p class="ra-rc-note">No lap times recorded for this round.</p>';
      return title + fin + motos.map((m) => `<h3 class="ra-rd-moto">${esc(m.l)}</h3>` + this.roundLaps(hit, x, m.rows, m.me, false)).join('') + foot;
    }

    /* Lap figures for one session. withFinish adds the round finish, points and gap tiles. */
    roundLaps(hit, x, rows, me, withFinish) {
      const lt = me.lt.filter((v) => typeof v === 'number' && v > 0);
      let b = me.b > 0 && me.b < lt.length ? me.b : null;
      if (b == null) for (let i = 1; i < lt.length; i++) if (b == null || lt[i] < lt[b]) b = i;
      const best = b != null ? lt[b] : null;
      const sorted = lt.slice().sort((p, q) => p - q);
      const mid = sorted.length % 2 ? sorted[(sorted.length - 1) / 2] : (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2;
      const avg = me.a != null ? me.a : lt.reduce((p, q) => p + q, 0) / lt.length;
      const timed = rows.filter((z) => z.lt && z.lt.length);
      const lead = rows.find((z) => z.p === 1);
      const cmp = lead && lead !== me ? lead : rows.find((z) => z.p === 2);
      const cbest = bestOf(rows);
      const tiles = (withFinish ? [
        ['Finish', x.p != null ? ordHTML(x.p) : esc(x.st || '–')],
        ['Points', ptsHTML(x.pts)],
        ['Gap to leader', esc(x.gap || '–')],
      ] : [
        ['Moto finish', me.p != null ? ordHTML(me.p) : esc(me.st || '–')],
      ]).concat([
        ['Best lap', best != null ? `${esc(fmtT(best))}<small>Lap ${esc(b + 1)}</small>` : '–'],
        ['Avg lap', esc(fmtT(avg))],
        ['Consistency', best != null ? `${esc('+' + (mid - best).toFixed(3) + ' sec')}<small>best to median</small>` : '–'],
      ]);
      const stats = `<dl class="ra-rc-stats ra-rd-stats">${tiles.map((t) => `<div><dt>${esc(t[0])}</dt><dd>${t[1]}</dd></div>`).join('')}</dl>`;
      const laps = lt.map((v, i) => `<li class="${i === b ? 'is-best' : ''}${cbest != null && Math.abs(v - cbest) < 0.0005 ? ' is-cbest' : ''}"><span>Lap ${esc(i + 1)}${i === 0 ? '<small>incl. start</small>' : ''}</span><b>${esc(fmtT(v))}</b><em>${i === b ? 'Best' : best != null ? esc(fmtDelta(v - best)) : ''}</em></li>`).join('');
      const posSeries = this.lapPositions(timed, me);
      return stats
        + `<figure class="ra-rd-fig"><figcaption><b>Lap times</b><span class="ra-rd-key"><i class="k-me"></i>${esc(hit.r.r)}${cmp && cmp.lt && cmp.lt.length ? `<i class="k-cmp"></i>${esc(cmp.p === 1 ? 'Class winner' : '2nd place')}` : ''}${b != null ? '<i class="k-best"></i>Best lap' : ''}</span></figcaption>${this.lapLine(lt, b, cmp && cmp.lt ? cmp.lt : null)}</figure>`
        + (posSeries ? `<figure class="ra-rd-fig"><figcaption><b>Position by lap</b><span class="ra-rd-key">Of ${esc(timed.length)} timed riders</span></figcaption>${posSeries}</figure>` : '')
        + `<ol class="ra-rd-laps">${laps}</ol>`;
    }

    /* Chart width in px so SVG text renders at its CSS size. */
    chartW() {
      const inn = this.pop && this.pop.querySelector('[data-ra-pop-in]');
      if (!inn || !inn.clientWidth) return 600;
      const cs = getComputedStyle(inn);
      const narrow = window.matchMedia('(max-width: 749px)').matches;
      this.popW = Math.max(260, Math.round(inn.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) - (narrow ? 14 : 26)));
      return this.popW;
    }

    lapLine(lt, b, cmp) {
      const W = this.chartW(), H = W < 500 ? 200 : 230, L = 50, R = 12, T = 12, B = 30;
      const c = cmp ? cmp.filter((v) => typeof v === 'number' && v > 0) : [];
      const n = Math.max(lt.length, c.length);
      const all = lt.concat(c);
      const mn = Math.min.apply(null, all), mx = Math.max.apply(null, all);
      const step = niceStep(Math.max(mx - mn, 1));
      const lo = Math.floor(mn / step) * step, hi = Math.max(Math.ceil(mx / step) * step, lo + step);
      const x = (i) => (n === 1 ? (L + W - R) / 2 : L + (i * (W - L - R)) / (n - 1));
      const y = (v) => T + ((v - lo) / (hi - lo)) * (H - T - B);
      const line = (arr) => arr.map((v, i) => x(i).toFixed(1) + ',' + y(v).toFixed(1)).join(' ');
      let grid = '';
      for (let v = lo; v <= hi + 0.001; v += step) {
        grid += `<line class="ra-g" x1="${L}" x2="${W - R}" y1="${y(v).toFixed(1)}" y2="${y(v).toFixed(1)}"/><text class="ra-gy" x="${L - 8}" y="${(y(v) + 4).toFixed(1)}">${esc(fmtTick(v))}</text>`;
      }
      const ticks = lapTicks(n, W - L - R, 26).map((i) => `<text class="ra-gx" x="${x(i).toFixed(1)}" y="${H - 10}">${esc(i + 1)}</text>`).join('')
        + `<text class="ra-gy ra-gl" x="${L - 8}" y="${H - 10}">Lap</text>`;
      const area = `${x(0).toFixed(1)},${H - B} ${line(lt)} ${x(lt.length - 1).toFixed(1)},${H - B}`;
      const gid = 'ra-lg-' + this.el.id + '-' + (this.lgN = (this.lgN || 0) + 1);
      const dots = lt.map((v, i) => `<circle class="${i === b ? 'is-best' : ''}" cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="${i === b ? 6 : 3.5}"><title>Lap ${esc(i + 1)}: ${esc(fmtT(v))}</title></circle>`).join('');
      return `<svg class="ra-rd-svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" role="img" aria-label="Lap times by lap, faster laps higher.${b != null ? ` Best ${esc(fmtT(lt[b]))} on lap ${esc(b + 1)}.` : ''}">
        <defs><linearGradient id="${esc(gid)}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" class="ra-lg-a"/><stop offset="1" class="ra-lg-b"/></linearGradient></defs>
        ${grid}${ticks}
        ${c.length > 1 ? `<polyline class="ra-cmp" points="${line(c)}"/>` : ''}
        <polygon class="ra-area" points="${area}" style="fill:url(#${esc(gid)})"/>
        ${b != null ? `<line class="ra-bestln" x1="${L}" x2="${W - R}" y1="${y(lt[b]).toFixed(1)}" y2="${y(lt[b]).toFixed(1)}"/>` : ''}
        <polyline class="ra-me" points="${line(lt)}"/>${dots}</svg>`;
    }

    /* Running order after each lap from cumulative lap times of every timed rider. */
    lapPositions(timed, me) {
      if (!me || timed.length < 2 || me.lt.length < 2) return '';
      const cum = timed.map((z) => { let t = 0; return { z, c: z.lt.map((v) => (t += v)) }; });
      const mine = cum.find((q) => q.z === me);
      const pos = mine.c.map((t, i) => 1 + cum.filter((q) => q !== mine && q.c.length > i && q.c[i] < t).length);
      const N = timed.length, n = pos.length;
      const worst = Math.max.apply(null, pos);
      const rows = Math.min(N, Math.max(worst, 2));
      const ROW = 24, W = this.chartW(), L = 46, R = 22, T = 24, B = 30;
      const H = T + (rows - 1) * ROW + B;
      const x = (i) => L + (i * (W - L - R)) / (n - 1);
      const y = (p) => T + (p - 1) * ROW;
      let d = `M${x(0).toFixed(1)},${y(pos[0]).toFixed(1)}`;
      for (let i = 1; i < n; i++) d += ` H${x(i).toFixed(1)} V${y(pos[i]).toFixed(1)}`;
      let grid = '';
      for (let p = 1; p <= rows; p++) {
        grid += `<line class="ra-g" x1="${L}" x2="${W - R}" y1="${y(p)}" y2="${y(p)}"/><text class="ra-gy${pos.includes(p) ? ' is-on' : ''}" x="${L - 8}" y="${y(p) + 4}">${esc(p + ordSuffix(p))}</text>`;
      }
      const ticks = lapTicks(n, W - L - R, 26).map((i) => `<text class="ra-gx" x="${x(i).toFixed(1)}" y="${H - 10}">${esc(i + 1)}</text>`).join('')
        + `<text class="ra-gy ra-gl" x="${L - 8}" y="${H - 10}">Lap</text>`;
      const placed = [];
      const free = (bx) => placed.every((q) => bx.r <= q.l || bx.l >= q.r || bx.b <= q.t || bx.t >= q.b);
      const dots = pos.map((p, i) => {
        const cx = x(i), cy = y(p);
        let show = i === 0 || i === n - 1 || p !== pos[i - 1];
        let tag = '';
        if (show) {
          const s = p + ordSuffix(p);
          const hw = s.length * 4 + 3;
          const lx = Math.max(cx, L + hw - 4);
          const opts = [{ ty: cy - 9, t: cy - 20, b: cy - 6 }];
          if (cy + 20 <= H - B + 8) opts.push({ ty: cy + 17, t: cy + 5, b: cy + 20 });
          const pick = opts.map((o) => ({ ty: o.ty, l: lx - hw, r: lx + hw, t: o.t, b: o.b })).find(free);
          if (pick) {
            placed.push(pick);
            tag = `<text class="ra-pl" x="${lx.toFixed(1)}" y="${pick.ty.toFixed(1)}">${esc(s)}</text>`;
          } else show = false;
        }
        return `<g class="ra-pt${show ? ' is-tag' : ''}"><circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${show ? 4.5 : 3.5}"><title>Lap ${esc(i + 1)}: ${esc(p + ordSuffix(p))}</title></circle>${tag}</g>`;
      }).join('');
      return `<svg class="ra-rd-svg ra-rd-pos" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" role="img" aria-label="Running position after each lap: ${esc(pos.map((p, i) => 'lap ' + (i + 1) + ' ' + p + ordSuffix(p)).join(', '))}.">${grid}${ticks}<path class="ra-me" d="${d}"/>${dots}</svg>`;
    }

    popVersus(hit, rounds) {
      const sd = standingsFor(hit.c, this.dropN());
      const rank = (x) => sd.info.get(x).pos || 9999;
      const field = hit.c.rows.slice().sort((a, b) => rank(a) - rank(b));
      const me = hit.r;
      const i = field.indexOf(me);
      let vs = field.find((x) => slugOf(x) === this.popVs && x !== me);
      if (!vs) vs = field[i > 0 ? i - 1 : 1];
      if (!vs || vs === me) return '<p class="ra-rc-note">No other rider in this class to compare with yet.</p>';
      this.popVs = slugOf(vs);
      const theirs = this.riderRounds(hit, vs);
      const opts = field.filter((x) => x !== me).map((x) => `<option value="${esc(slugOf(x))}"${x === vs ? ' selected' : ''}>${esc(rank(x) < 9999 ? rank(x) + ordSuffix(rank(x)) + ' · ' : '')}#${esc(x.n)} ${esc(x.r)}</option>`).join('');
      let a = 0, b = 0, shared = 0;
      const rows = rounds.map((x, k) => {
        const y = theirs[k];
        let win = '';
        if (x.state === 'done') {
          if (x.p != null && y.p != null) { shared++; if (x.p < y.p) { a++; win = 'a'; } else if (y.p < x.p) { b++; win = 'b'; } }
          else if (x.p != null) win = 'a';
          else if (y.p != null) win = 'b';
        }
        const side = (z, w) => `<td class="ra-vs-side${win === w ? ' is-ahead' : ''}${z.drop ? ' is-drop' : ''}">${this.finishHTML(z)}${z.state === 'done' ? `<small>${ptsHTML(z.pts)}${z.best ? ' · ' + esc(z.best) : ''}</small>` : ''}</td>`;
        return `<tr class="is-${esc(x.state)}">${side(x, 'a')}<th scope="row" class="ra-vs-rd">${this.roundLabel(x)}</th>${side(y, 'b')}</tr>`;
      }).join('');
      const tot = (z) => sd.info.get(z).total;
      const diff = tot(me) - tot(vs);
      return `<div class="ra-vs-pick"><label>Compare with <select data-ra-vs>${opts}</select></label></div>
        <div class="ra-vs-head">
          <div class="ra-vs-p"><span class="ra-plate">${esc(me.n)}</span><b>${esc(me.r)}</b><span>${ptsHTML(tot(me))}</span></div>
          <div class="ra-vs-mid"><span class="ra-vs-x">VS</span><span class="ra-vs-score"><b>${esc(a)}</b>–<b>${esc(b)}</b></span><small>${esc(shared)} shared round${shared === 1 ? '' : 's'}</small></div>
          <div class="ra-vs-p ra-vs-p--b"><button type="button" class="ra-plate" data-ra-rider="${esc(slugOf(vs))}" title="Open ${esc(vs.r)}">${esc(vs.n)}</button><b>${esc(vs.r)}</b><span>${ptsHTML(tot(vs))}</span></div>
        </div>
        <p class="ra-vs-sum">${diff === 0 ? 'Level on points.' : `${esc(diff > 0 ? me.r : vs.r)} leads by ${esc(Math.abs(diff))} pts.`}</p>
        <div class="ra-rc-tw"><table class="ra-vs-t"><tbody>${rows}</tbody></table></div>`;
    }


    scrollToBar() {
      const bar = this.root.querySelector('.ra-bar');
      if (!bar) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      bar.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }
  }

  function boot() {
    document.querySelectorAll('[data-results-app]').forEach((el) => {
      if (el.raApp) return;
      el.raApp = new ResultsApp(el);
      el.raApp.init();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
